import React, { useState, useEffect, useCallback, useRef } from "react";
import { ref as refApi, whatsapp as waApi, clientGroups as cgApi, marketerGroups as mgApi, projects as projectsApi, communication as commApi, prospects as prospectsApi, addWithDupGuard } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  FiSend, FiSearch, FiPaperclip, FiX, FiCheck, FiCheckCircle,
  FiAlertCircle, FiRefreshCw, FiUsers, FiUserCheck,
  FiRadio, FiChevronLeft, FiImage, FiFile,
  FiWifi, FiWifiOff
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

// ═══════════════════════════════════════════════════════════════
// Phone Helpers
// ═══════════════════════════════════════════════════════════════
const normalizePhone = (phone) => {
  if (!phone) return "";
  let p = phone.replace(/[\s\-().+]/g, "").replace(/@.*$/, "");
  if (p.startsWith("00")) p = p.slice(2);
  if (p.startsWith("0"))  p = "966" + p.slice(1);
  if (!p.startsWith("966")) p = "966" + p;
  return p; // plain number e.g. "966501234567"
};

const displayPhone = (phone) => {
  if (!phone) return "";
  let p = String(phone).replace(/[\s\-().+]/g, "");
  // v3.3.790 — معرّفات LID (خصوصية واتساب الجديدة) ليست أرقام هواتف — لا تُعرض كرقم
  if (p.length >= 14) return "🔒 رقم مخفي (خصوصية واتساب)";
  if (p.startsWith("00")) p = p.slice(2);
  if (p.startsWith("0"))  p = "966" + p.slice(1);
  if (!p.startsWith("966")) p = "966" + p;
  return "+" + p;
};

const hasPhone = (c) => !!(c?.phone || c?.mobile);
const getPhone = (c) => c?.phone || c?.mobile || "";

// ═══════════════════════════════════════════════════════════════
// Message Templates
// ═══════════════════════════════════════════════════════════════
// Templates are now loaded from DB — see loadTemplates in main component
// Variables: {الاسم} {المشروع} {التاريخ}
const CLIENT_TEMPLATES_FALLBACK = [
  { icon: "👋", label: "ترحيب",        text: "السلام عليكم {الاسم}، نرحب بكم في رائل للعقارات. نسعد بخدمتكم!" },
  { icon: "✅", label: "تأكيد حجز",   text: "مرحباً {الاسم}، يسعدنا إبلاغكم بأنه تم تأكيد حجزكم بنجاح. شكراً لثقتكم." },
  { icon: "💰", label: "تذكير دفعة",  text: "مرحباً {الاسم}، نودّ تذكيركم بموعد الدفعة المستحقة. نرجو التواصل معنا." },
];

const MARKETER_TEMPLATES_FALLBACK = [
  { icon: "🏢", label: "ترحيب مسوق",  text: "السلام عليكم {الاسم}، مرحباً بكم في شراكتنا مع رائل للعقارات!" },
  { icon: "📊", label: "تحديث مشروع", text: "مرحباً {الاسم}، نود إبلاغكم بآخر مستجدات المشروع. يرجى التواصل للمزيد." },
  { icon: "💼", label: "عرض جديد",    text: "مرحباً {الاسم}، لدينا عروض حصرية جديدة متاحة. هل أنتم مهتمون بالتفاصيل؟" },
];

const personalize = (text, contact, extra = {}) =>
  text
    .replace(/\{الاسم\}/g, contact?.name || "")
    .replace(/\{name\}/g,  contact?.name || "")
    .replace(/\{المشروع\}/g, extra?.projectName || "")
    .replace(/\{التاريخ\}/g, new Date().toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" }))
    .replace(/\{تاريخ_اليوم\}/g, new Date().toISOString().slice(0, 10));

// ═══════════════════════════════════════════════════════════════
// StatusPill
// ═══════════════════════════════════════════════════════════════
const StatusPill = ({ status }) => {
  const cfg = {
    connected:    { color: "#16a34a", bg: "#dcfce7", label: "متصل",         icon: <FiWifi size={11}/> },
    disconnected: { color: "#dc2626", bg: "#fee2e2", label: "غير متصل",      icon: <FiWifiOff size={11}/> },
    loading:      { color: "#d97706", bg: "#fef3c7", label: "جاري الفحص...", icon: <span className="wa-spin">↻</span> },
  };
  const c = cfg[status] || cfg.loading;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:5,
      background:c.bg, color:c.color,
      padding:"3px 10px", borderRadius:20, fontSize:12, fontWeight:600
    }}>
      {c.icon} {c.label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════
// MessageBubble
// ═══════════════════════════════════════════════════════════════
const MEDIA_CONFIG = {
  image:    { icon: "🖼️", label: "صورة",          color: "#3b82f6" },
  video:    { icon: "🎥", label: "فيديو",         color: "#8b5cf6" },
  audio:    { icon: "🎵", label: "رسالة صوتية",   color: "#10b981" },
  ptt:      { icon: "🎤", label: "رسالة صوتية",   color: "#10b981" },
  document: { icon: "📄", label: "مستند",         color: "#f59e0b" },
  sticker:  { icon: "😊", label: "ملصق",          color: "#ec4899" },
  location: { icon: "📍", label: "موقع",          color: "#ef4444" },
  contact:  { icon: "👤", label: "جهة اتصال",     color: "#64748b" },
};

const SENDER_COLORS = ["#e11d48","#7c3aed","#0891b2","#c2410c","#4f46e5","#0d9488","#be185d","#b45309"];
const getSenderColor = (name) => {
  if (!name) return SENDER_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash) + name.charCodeAt(i);
  return SENDER_COLORS[Math.abs(hash) % SENDER_COLORS.length];
};

// ═══════════════════════════════════════════════════════════════
// MediaContent — يحمّل ويعرض الميديا (صور، صوت، فيديو، ملفات)
// ═══════════════════════════════════════════════════════════════
function MediaContent({ msg, session }) {
  const [media, setMedia] = useState(null);      // { mimetype, data, filename }
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const msgId = msg.id;
  const type = msg.type || "chat";
  const mediaInfo = MEDIA_CONFIG[type] || { icon: "📎", label: "ملف مرفق", color: "#64748b" };

  const loadMedia = useCallback(async () => {
    if (!msgId || !session || loading || media) return;
    setLoading(true); setError(null);
    try {
      const res = await waApi.getMedia(session, msgId);
      if (res.data?.ok && res.data?.data) {
        setMedia(res.data);
      } else {
        setError("غير متوفر");
      }
    } catch {
      setError("غير متوفر");
    }
    setLoading(false);
  }, [msgId, session, loading, media]);

  // Auto-load for images and stickers (small)
  useEffect(() => {
    if ((type === "image" || type === "sticker") && msgId && session && !msgId.startsWith("opt_") && !msgId.startsWith("gen_")) {
      loadMedia();
    }
  }, [type, msgId, session]); // eslint-disable-line

  // ─── صورة ──
  if (type === "image" || type === "sticker") {
    if (media?.data) {
      return (
        <div style={{ marginBottom: msg.body ? 8 : 0 }}>
          <img
            src={`data:${media.mimetype};base64,${media.data}`}
            alt="صورة"
            style={{ maxWidth:"100%", maxHeight:300, borderRadius:8, cursor:"pointer", display:"block" }}
            onClick={() => {
              const w = window.open();
              w.document.write(`<img src="data:${media.mimetype};base64,${media.data}" style="max-width:100%;margin:20px auto;display:block"/>`);
            }}
          />
        </div>
      );
    }
    return (
      <div onClick={loadMedia} style={{
        background:"#f1f5f9", borderRadius:10, padding:"20px 14px", textAlign:"center",
        marginBottom: msg.body ? 8 : 0, cursor: loading ? "wait" : "pointer",
        border:"1px dashed #cbd5e1"
      }}>
        {loading ? (
          <span style={{ color:"#94a3b8", fontSize:13 }}><span className="wa-spin">↻</span> جاري التحميل...</span>
        ) : error ? (
          <span style={{ color:"#94a3b8", fontSize:12 }}>🖼️ صورة — اضغط لإعادة المحاولة</span>
        ) : (
          <span style={{ color:"#94a3b8", fontSize:12 }}>🖼️ جاري تحميل الصورة...</span>
        )}
      </div>
    );
  }

  // ─── صوت / رسالة صوتية ──
  if (type === "audio" || type === "ptt") {
    if (media?.data) {
      return (
        <div style={{ marginBottom: msg.body ? 8 : 0 }}>
          <audio controls style={{ width:"100%", maxWidth:280, height:36 }}
            src={`data:${media.mimetype};base64,${media.data}`}/>
        </div>
      );
    }
    return (
      <div onClick={loadMedia} style={{
        background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10,
        padding:"10px 14px", display:"flex", alignItems:"center", gap:10,
        cursor: loading ? "wait" : "pointer", marginBottom: msg.body ? 8 : 0,
      }}>
        <span style={{ fontSize:22 }}>🎤</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:"#10b981" }}>رسالة صوتية</div>
          <div style={{ fontSize:11, color:"#94a3b8" }}>
            {loading ? "جاري التحميل..." : error ? "اضغط لإعادة المحاولة" : "اضغط للتشغيل"}
          </div>
        </div>
      </div>
    );
  }

  // ─── فيديو ──
  if (type === "video") {
    if (media?.data) {
      return (
        <div style={{ marginBottom: msg.body ? 8 : 0 }}>
          <video controls style={{ maxWidth:"100%", maxHeight:250, borderRadius:8, display:"block" }}
            src={`data:${media.mimetype};base64,${media.data}`}/>
        </div>
      );
    }
    return (
      <div onClick={loadMedia} style={{
        background:"#f5f3ff", border:"1px solid #c4b5fd", borderRadius:10,
        padding:"14px", textAlign:"center", cursor: loading ? "wait" : "pointer",
        marginBottom: msg.body ? 8 : 0,
      }}>
        <span style={{ fontSize:28 }}>🎥</span>
        <div style={{ fontSize:12, color:"#7c3aed", marginTop:4 }}>
          {loading ? "جاري التحميل..." : error ? "اضغط لإعادة المحاولة" : "اضغط لتحميل الفيديو"}
        </div>
      </div>
    );
  }

  // ─── مستند / ملف ──
  if (type === "document") {
    if (media?.data) {
      const ext = (media.filename || "").split(".").pop()?.toLowerCase() || "ملف";
      return (
        <div style={{
          background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10,
          padding:"10px 14px", display:"flex", alignItems:"center", gap:10,
          marginBottom: msg.body ? 8 : 0,
        }}>
          <span style={{ fontSize:22 }}>📄</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:"#b45309", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {media.filename || "مستند"}
            </div>
            <div style={{ fontSize:11, color:"#94a3b8" }}>{ext.toUpperCase()}</div>
          </div>
          <a
            href={`data:${media.mimetype};base64,${media.data}`}
            download={media.filename || `document.${ext}`}
            style={{ background:"#f59e0b", color:"#fff", padding:"4px 10px", borderRadius:6, fontSize:11, textDecoration:"none", fontWeight:600 }}
          >تحميل</a>
        </div>
      );
    }
    return (
      <div onClick={loadMedia} style={{
        background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10,
        padding:"10px 14px", display:"flex", alignItems:"center", gap:10,
        cursor: loading ? "wait" : "pointer", marginBottom: msg.body ? 8 : 0,
      }}>
        <span style={{ fontSize:22 }}>{error ? "⚠️" : "📄"}</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color: error ? "#dc2626" : "#f59e0b" }}>مستند</div>
          <div style={{ fontSize:11, color: error ? "#dc2626" : "#94a3b8" }}>
            {loading ? "⏳ جاري التحميل..." : error
              ? "تعذر جلب الملف من سيرفر الواتساب — اضغط لإعادة المحاولة"
              : "اضغط لتحميل الملف"}
          </div>
        </div>
      </div>
    );
  }

  // ─── أنواع أخرى (موقع، جهة اتصال، ملصق) ──
  return (
    <div style={{
      background: `${mediaInfo.color}15`, border: `1px solid ${mediaInfo.color}40`,
      borderRadius:10, padding:"10px 14px", marginBottom: msg.body ? 8 : 0,
      display:"flex", alignItems:"center", gap:10,
    }}>
      <span style={{ fontSize:22 }}>{mediaInfo.icon}</span>
      <div style={{ fontSize:13, fontWeight:600, color: mediaInfo.color }}>{mediaInfo.label}</div>
    </div>
  );
}

// ─── vCard parser ─────────────────────────────────────────────
function parseVCard(text) {
  if (!text || !text.includes("BEGIN:VCARD")) return null;
  const lines = text.split(/\r?\n/);
  let name = "", phone = "";
  for (const line of lines) {
    if (line.startsWith("FN:")) name = line.slice(3).trim();
    if (!name && line.startsWith("N:")) {
      const parts = line.slice(2).split(";").filter(Boolean);
      name = parts.reverse().join(" ").trim();
    }
    // TEL lines: TEL;... :+966... or item1.TEL;waid=966...:+966...
    const telMatch = line.match(/(?:TEL|\.TEL)[^:]*:\+?(\d[\d\s-]+)/i);
    if (telMatch && !phone) {
      phone = telMatch[1].replace(/[\s-]/g, "");
    }
  }
  if (!name && !phone) return null;
  return { name, phone };
}

function VCardActions({ vcard }) {
  const [adding, setAdding] = useState(null); // "client" | "marketer"
  const [done, setDone] = useState(null);     // "client" | "marketer"

  const handleAdd = async (type) => {
    setAdding(type);
    try {
      const addFn = type === "client" ? refApi.addClient : refApi.addMarketer;
      const data = { name: vcard.name, phone: vcard.phone };
      await addWithDupGuard(addFn, data); // v3.3.741 — حارس التكرار
      setDone(type);
      toast.success(`تم إضافة "${vcard.name}" كـ${type === "client" ? "عميل" : "مسوق"} ✓`);
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message;
      if (errMsg?.includes("duplicate") || errMsg?.includes("already") || errMsg?.includes("موجود")) {
        toast.error(`"${vcard.name}" موجود مسبقاً`);
      } else {
        toast.error("فشل الإضافة: " + errMsg);
      }
    }
    setAdding(null);
  };

  return (
    <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
      <button onClick={() => handleAdd("client")} disabled={!!adding || done === "client"}
        style={{
          padding:"5px 12px", borderRadius:8, border:"1px solid #3b82f6",
          background: done === "client" ? "#dbeafe" : "#fff", color:"#2563eb",
          fontSize:11, fontWeight:600, cursor: adding ? "wait" : "pointer",
          fontFamily:"inherit", display:"flex", alignItems:"center", gap:4,
          opacity: adding === "marketer" ? 0.5 : 1,
        }}>
        {done === "client" ? "✓ تمت الإضافة" : adding === "client" ? "جاري..." : "👤 إضافة كعميل"}
      </button>
      <button onClick={() => handleAdd("marketer")} disabled={!!adding || done === "marketer"}
        style={{
          padding:"5px 12px", borderRadius:8, border:"1px solid #a21caf",
          background: done === "marketer" ? "#fae8ff" : "#fff", color:"#a21caf",
          fontSize:11, fontWeight:600, cursor: adding ? "wait" : "pointer",
          fontFamily:"inherit", display:"flex", alignItems:"center", gap:4,
          opacity: adding === "client" ? 0.5 : 1,
        }}>
        {done === "marketer" ? "✓ تمت الإضافة" : adding === "marketer" ? "جاري..." : "📢 إضافة كمسوق"}
      </button>
    </div>
  );
}

// v3.3.707 — تحويل الروابط داخل نصوص الرسائل لروابط قابلة للنقر
const URL_RE = /(https?:\/\/[^\s]+)/g;
function LinkifiedText({ text, style }) {
  const parts = String(text).split(URL_RE);
  return (
    <div style={style}>
      {parts.map((p, i) => URL_RE.test(p) && p.startsWith("http")
        ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", wordBreak: "break-all" }}>{p}</a>
        : <React.Fragment key={i}>{p}</React.Fragment>)}
    </div>
  );
}

// v3.3.707 — رسائل "[ملف: ...]" (سجل إرسال الملفات) تُعرض كبطاقة ملف بدل نص خام
// v3.3.716 — تشمل أيضًا صيغة "[ملف Drive: ...]" (النشر السريع لتقارير المتاح)
function SentFileBody({ body }) {
  const m = body.match(/^\[ملف(?: Drive)?:\s*([^\]]+)\]\s*(?:—\s*)?([\s\S]*)$/);
  const fileName = m ? m[1].trim() : "ملف مرفق";
  const rest = m ? (m[2] || "").trim() : "";
  const urlMatch = body.match(URL_RE);
  return (
    <div>
      <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10,
        padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, marginBottom: rest ? 8 : 0 }}>
        <span style={{ fontSize: 22 }}>📎</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#b45309", overflowWrap: "anywhere" }}>{fileName}</div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>ملف مُرسل عبر الواتساب</div>
        </div>
        {urlMatch && (
          <a href={urlMatch[0]} target="_blank" rel="noopener noreferrer"
            style={{ background: "#f59e0b", color: "#fff", padding: "4px 10px", borderRadius: 6, fontSize: 11, textDecoration: "none", fontWeight: 600, flexShrink: 0 }}>فتح</a>
        )}
      </div>
      {rest && <LinkifiedText text={rest} style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} />}
    </div>
  );
}

const MessageBubble = ({ msg, contactPhone, isGroupChat, session }) => {
  const isOut = msg.fromMe;
  const time = msg.timestamp
    ? new Date(msg.timestamp * 1000).toLocaleTimeString("ar-SA", { hour:"2-digit", minute:"2-digit" })
    : "";
  const hasMedia = msg.hasMedia || msg.hasMedia === true || Object.keys(MEDIA_CONFIG).includes(msg.type);
  const senderName = msg.senderName || msg.sender_name;
  const showSender = isGroupChat && !isOut && senderName;

  // كشف vCard
  const vcard = parseVCard(msg.body);

  // v3.3.706 — رسائل الوسائط أحيانًا يحمل body مفتاح الوسائط الخام (نص طويل متكرر بلا
  // مسافات ولا عربية) فيظهر مشوهًا تحت بطاقة المستند — نخفيه ونبقي البطاقة فقط
  const isGarbageMediaBody = hasMedia && msg.body && msg.body.length > 40
    && !/\s/.test(msg.body.trim())
    && !/[؀-ۿ]/.test(msg.body)
    && !/^https?:/i.test(msg.body.trim());
  const displayBody = isGarbageMediaBody ? "" : msg.body;

  return (
    <div style={{ display:"flex", justifyContent: isOut ? "flex-end" : "flex-start", marginBottom:6 }}>
      <div style={{
        maxWidth:"72%",
        background: isOut ? "#dcf8c6" : "#fff",
        borderRadius: isOut ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
        padding:"10px 14px", boxShadow:"0 1px 3px rgba(0,0,0,0.08)"
      }}>
        {showSender && (
          <div style={{ fontSize:12, fontWeight:700, color: getSenderColor(senderName), marginBottom:3 }}>
            {senderName}
          </div>
        )}
        {hasMedia && session && (
          <MediaContent msg={msg} session={session}/>
        )}
        {hasMedia && !session && (
          <div style={{
            background: `${(MEDIA_CONFIG[msg.type] || { color: "#64748b" }).color}15`,
            border: `1px solid ${(MEDIA_CONFIG[msg.type] || { color: "#64748b" }).color}40`,
            borderRadius:10, padding:"10px 14px", marginBottom: msg.body ? 8 : 0,
            display:"flex", alignItems:"center", gap:10,
          }}>
            <span style={{ fontSize:22 }}>{(MEDIA_CONFIG[msg.type] || { icon: "📎" }).icon}</span>
            <div style={{ fontSize:13, fontWeight:600, color: (MEDIA_CONFIG[msg.type] || { color: "#64748b" }).color }}>
              {(MEDIA_CONFIG[msg.type] || { label: "ملف مرفق" }).label}
            </div>
          </div>
        )}
        {vcard ? (
          <div>
            <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:10, padding:"10px 14px", marginBottom: 4 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:"#0ea5e9", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:13 }}>
                  {(vcard.name || "?")[0]}
                </div>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color:"#0c4a6e" }}>{vcard.name || "بدون اسم"}</div>
                  {vcard.phone && <div style={{ fontSize:12, color:"#0369a1", direction:"ltr" }}>+{vcard.phone}</div>}
                </div>
              </div>
              <VCardActions vcard={vcard} />
            </div>
            {msg.body && !msg.body.startsWith("BEGIN:VCARD") && (
              <div style={{ fontSize:14, color:"#1e293b", lineHeight:1.65, whiteSpace:"pre-wrap" }}>
                {msg.body}
              </div>
            )}
          </div>
        ) : displayBody ? (
          /^\[ملف(?: Drive)?:/.test(displayBody)
            ? <SentFileBody body={displayBody} />
            : <LinkifiedText text={displayBody}
                style={{ fontSize:14, color:"#1e293b", lineHeight:1.65, whiteSpace:"pre-wrap", overflowWrap:"anywhere" }} />
        ) : null}
        <div style={{
          fontSize:11, color:"#94a3b8", marginTop:4,
          display:"flex", alignItems:"center", gap:4, justifyContent:"flex-end"
        }}>
          {time}
          {isOut && (msg.ack >= 2
            ? <FiCheckCircle size={11} color="#34d399"/>
            : <FiCheck size={11}/>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ChatPanel
// ═══════════════════════════════════════════════════════════════
function ChatPanel({ session, contact, recipientType, templates, onBack }) {
  const [messages,  setMessages]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [sending,   setSending]   = useState(false);
  const [text,      setText]      = useState("");
  const [file,      setFile]      = useState(null);
  const [showTpl,   setShowTpl]   = useState(false);

  const endRef      = useRef(null);
  const fileRef     = useRef(null);
  const textareaRef = useRef(null);
  const pollRef     = useRef(null);

  const phone = normalizePhone(getPhone(contact));

  const loadMessages = useCallback(async () => {
    if (!phone) return;
    try {
      const res = await waApi.getMessages(session, phone, 60);
      setMessages(res.data?.messages || res.data || []);
    } catch { /* silent poll */ }
    setLoading(false);
  }, [session, phone]);

  useEffect(() => {
    setMessages([]); setLoading(true);
    loadMessages();
    pollRef.current = setInterval(loadMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadMessages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  const applyTemplate = (t) => {
    setText(personalize(t.text, contact));
    setShowTpl(false);
    textareaRef.current?.focus();
  };

  const sendMessage = async () => {
    if (sending || (!text.trim() && !file)) return;
    if (!phone) { toast.error("لا يوجد رقم جوال صالح"); return; }
    setSending(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("phone", phone);
        fd.append("file", file);
        fd.append("recipientType", recipientType || "client");
        fd.append("recipientName", contact.name || "");
        if (text.trim()) fd.append("caption", text.trim());
        await waApi.sendFile(session, fd);
        toast.success("تم إرسال الملف ✓");
      } else {
        await waApi.sendMessage(session, phone, text.trim(), {
          recipientType,
          recipientName: contact.name || "",
        });
        // إضافة الرسالة فوراً للشاشة
        const optimistic = {
          id: `local_${Date.now()}`,
          body: text.trim(),
          fromMe: true,
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
        };
        setMessages(prev => [optimistic, ...prev]);
        toast.success("تم الإرسال ✓");
      }
      setText(""); setFile(null);
      setTimeout(loadMessages, 2000);
      setTimeout(loadMessages, 5000);
    } catch (e) {
      toast.error("فشل الإرسال: " + (e.response?.data?.error || e.message));
    }
    setSending(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#e5ddd5" }}>

      {/* Header */}
      <div style={{
        background:"#128C7E", color:"#fff", padding:"12px 16px",
        display:"flex", alignItems:"center", gap:12,
        boxShadow:"0 2px 8px rgba(0,0,0,0.15)", flexShrink:0
      }}>
        <button onClick={onBack}
          style={{ background:"none", border:"none", cursor:"pointer", color:"#fff", padding:4 }}>
          <FiChevronLeft size={22}/>
        </button>
        <div style={{
          width:42, height:42, borderRadius:"50%",
          background:"rgba(255,255,255,0.25)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontWeight:700, fontSize:17, flexShrink:0
        }}>{(contact.name||"?")[0]}</div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{contact.name}</div>
          <div style={{ fontSize:12, opacity:0.8, direction:"ltr", textAlign:"right" }}>
            {displayPhone(getPhone(contact))}
          </div>
        </div>
        <button onClick={loadMessages}
          style={{ background:"none", border:"none", cursor:"pointer", color:"#fff", padding:4 }}>
          <FiRefreshCw size={15}/>
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex:1, overflowY:"auto", padding:"14px 12px" }}>
        {loading && messages.length === 0 ? (
          <div style={{ textAlign:"center", color:"#888", padding:40, fontSize:14 }}>
            <span className="wa-spin" style={{ fontSize:28, display:"block", marginBottom:10 }}>↻</span>
            جاري تحميل المحادثة...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign:"center", color:"#888", padding:60 }}>
            <FaWhatsapp size={38} style={{ opacity:0.2, marginBottom:10 }}/><br/>لا توجد رسائل بعد
          </div>
        ) : (
          [...messages].reverse().map((msg, i) => (
            <MessageBubble key={msg.id || i} msg={msg} contactPhone={phone} session={session}/>
          ))
        )}
        <div ref={endRef}/>
      </div>

      {/* Template drawer */}
      {showTpl && (
        <div style={{
          background:"#fff", borderTop:"1px solid #e2e8f0",
          padding:"10px 12px", maxHeight:210, overflowY:"auto", flexShrink:0
        }}>
          <div style={{ fontSize:12, fontWeight:600, color:"#64748b", marginBottom:8 }}>قوالب الرسائل:</div>
          {templates.map((t, i) => (
            <div key={i} onClick={() => applyTemplate(t)} style={{
              padding:"8px 12px", borderRadius:8, cursor:"pointer", fontSize:13,
              display:"flex", alignItems:"center", gap:9, marginBottom:4,
              background:"#f8fafc",
            }}
            onMouseEnter={e => e.currentTarget.style.background="#f0fdf4"}
            onMouseLeave={e => e.currentTarget.style.background="#f8fafc"}
            >
              <span style={{ fontSize:18, flexShrink:0 }}>{t.icon}</span>
              <div>
                <div style={{ fontWeight:600 }}>{t.label}</div>
                <div style={{ color:"#94a3b8", fontSize:12, marginTop:1 }}>
                  {personalize(t.text, contact).slice(0, 55)}...
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File preview */}
      {file && (
        <div style={{
          background:"#fff", padding:"8px 16px", borderTop:"1px solid #e2e8f0",
          display:"flex", alignItems:"center", gap:10, flexShrink:0
        }}>
          <FiFile size={15} color="#128C7E"/>
          <span style={{ flex:1, fontSize:13, color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {file.name}
          </span>
          <button onClick={() => setFile(null)}
            style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444" }}>
            <FiX size={15}/>
          </button>
        </div>
      )}

      {/* Input bar */}
      <div style={{
        background:"#f0f0f0", padding:"10px 12px",
        display:"flex", alignItems:"flex-end", gap:8, flexShrink:0
      }}>
        <button onClick={() => setShowTpl(v => !v)} style={{
          width:40, height:40, borderRadius:"50%", border:"none",
          background: showTpl ? "#dcfce7" : "#fff",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          color: showTpl ? "#16a34a" : "#64748b",
          boxShadow:"0 1px 3px rgba(0,0,0,0.1)", flexShrink:0, fontSize:18
        }} title="قوالب">⚡</button>

        <button onClick={() => fileRef.current?.click()} style={{
          width:40, height:40, borderRadius:"50%", border:"none", background:"#fff",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          color:"#64748b", boxShadow:"0 1px 3px rgba(0,0,0,0.1)", flexShrink:0
        }}><FiPaperclip size={17}/></button>
        <input ref={fileRef} type="file" style={{ display:"none" }}
          accept="image/*,.pdf,.doc,.docx,.xlsx,.xls"
          onChange={e => setFile(e.target.files[0] || null)}/>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          placeholder="اكتب رسالة..."
          rows={1}
          style={{
            flex:1, padding:"10px 16px", border:"none", borderRadius:22,
            fontFamily:"inherit", fontSize:14, resize:"none", outline:"none",
            lineHeight:1.5, maxHeight:120, overflowY:"auto",
            boxShadow:"0 1px 3px rgba(0,0,0,0.1)"
          }}
          onInput={e => {
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
          }}
        />

        <button
          onClick={sendMessage}
          disabled={sending || (!text.trim() && !file)}
          style={{
            width:42, height:42, borderRadius:"50%", border:"none",
            background: (!sending && (text.trim() || file)) ? "#128C7E" : "#94a3b8",
            cursor: (!sending && (text.trim() || file)) ? "pointer" : "not-allowed",
            display:"flex", alignItems:"center", justifyContent:"center",
            flexShrink:0, color:"#fff", boxShadow:"0 2px 8px rgba(18,140,126,0.3)"
          }}
        >
          {sending
            ? <span className="wa-spin" style={{ fontSize:18 }}>↻</span>
            : <FiSend size={17}/>
          }
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BroadcastTab — بث جماعي (تاب مدمج بدل popup)
// ═══════════════════════════════════════════════════════════════
function BroadcastTab({ session, recipientType, contacts, templates }) {
  const [selected,  setSelected]  = useState(new Set());
  const [selGroups, setSelGroups] = useState(new Set());
  const [message,   setMessage]   = useState("");
  const [files,     setFiles]     = useState([]);   // مصفوفة ملفات بدل ملف واحد
  const [search,    setSearch]    = useState("");
  const [sending,   setSending]   = useState(false);
  const [results,   setResults]   = useState([]);
  const [started,   setStarted]   = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [mode,      setMode]      = useState("contacts"); // "contacts" | "clientGroups" | "waGroups"
  const [groups,    setGroups]    = useState([]); // واتساب groups
  const [loadingGrp, setLoadingGrp] = useState(false);
  const [clientGroups, setClientGroups] = useState([]); // مجموعات العملاء
  const [selClientGroups, setSelClientGroups] = useState(new Set());
  const [clientGroupMembers, setClientGroupMembers] = useState({}); // {groupId: [members]}
  const [loadingCG, setLoadingCG] = useState(false);
  const [dailyStatus, setDailyStatus] = useState({ count: 0, limit: 50, remaining: 50, blocked: false });
  const fileRef = useRef(null);
  // v3.3.599 — inline edit of the daily WhatsApp limit (admins / settings perm)
  const { user } = useAuth();
  const canEditLimit = ((user?.role === "admin" && !user?.impersonating)) || !!user?.can_view_settings;
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const saveDailyLimit = async () => {
    const v = parseInt(limitInput);
    if (!Number.isFinite(v) || v < 1) { toast.error("أدخل رقماً صحيحاً"); return; }
    try {
      const r = await waApi.setDailyLimit(v);
      setDailyStatus(r.data);
      setEditingLimit(false);
      toast.success(`تم تعيين الحد اليومي إلى ${v}`);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل تعديل الحد");
    }
  };

  // ═══ Project Attachment States ═══
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDocs, setProjectDocs] = useState([]);
  const [selectedDriveFiles, setSelectedDriveFiles] = useState([]); // [{id, title, doc_type, drive_file_id}]
  const [loadingProjectDocs, setLoadingProjectDocs] = useState(false);

  // تحميل قائمة المشاريع
  useEffect(() => {
    projectsApi.list().then(r => {
      const list = r.data?.projects || r.data || [];
      setProjectsList(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  // تحميل مستندات المشروع المحدد
  const loadProjectDocs = async (projectId) => {
    if (!projectId) { setProjectDocs([]); setSelectedDriveFiles([]); return; }
    setLoadingProjectDocs(true);
    try {
      const r = await projectsApi.getProjectDocuments(projectId);
      const docs = r.data?.documents || [];
      // الملفات الترويجية (بروشور + فيديو + ملفات إضافية)
      const promoDocs = docs.filter(d => d.doc_category === "promotional" && d.doc_type !== "تقرير_توفر");
      // آخر 3 تحديثات/تقارير
      const updateDocs = docs.filter(d => d.doc_type === "تقرير_توفر").slice(0, 3);
      // v3.3.307: العروض التسويقية
      const offers = docs.filter(d => d.doc_category === "offer");
      setProjectDocs([...promoDocs, ...updateDocs, ...offers]);
    } catch { setProjectDocs([]); }
    setLoadingProjectDocs(false);
  };

  // جلب حالة العداد اليومي
  useEffect(() => {
    waApi.getDailyStatus().then(r => setDailyStatus(r.data)).catch(() => {});
  }, []);
  // تحديث العداد بعد كل إرسال
  const refreshDailyStatus = () => { waApi.getDailyStatus().then(r => setDailyStatus(r.data)).catch(() => {}); };

  const addFiles = (newFiles) => {
    if (!newFiles?.length) return;
    setFiles(prev => [...prev, ...Array.from(newFiles)]);
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  // سحب مجموعات العملاء أو المسوقين حسب النوع
  const groupsApi = recipientType === "marketer" ? mgApi : cgApi;
  const groupsLabel = recipientType === "marketer" ? "مجموعات المسوقين" : "مجموعات العملاء";

  useEffect(() => {
    (async () => {
      setLoadingCG(true);
      try {
        const res = await groupsApi.list();
        setClientGroups(res.data?.groups || []);
      } catch {}
      setLoadingCG(false);
    })();
  }, [recipientType]);

  // سحب أعضاء المجموعات المحددة
  useEffect(() => {
    (async () => {
      for (const gid of selClientGroups) {
        if (!clientGroupMembers[gid]) {
          try {
            const res = await groupsApi.getMembers(gid);
            setClientGroupMembers(prev => ({ ...prev, [gid]: res.data?.members || [] }));
          } catch {}
        }
      }
    })();
  }, [selClientGroups]);

  // سحب جروبات الواتساب
  useEffect(() => {
    (async () => {
      setLoadingGrp(true);
      try {
        const res = await waApi.getInbox(session);
        const chats = res.data?.chats || [];
        setGroups(chats.filter(c => c.isGroup));
      } catch {
        // fallback: try WA chats endpoint
        try {
          const res = await waApi.getChats(session);
          const chats = res.data?.chats || [];
          setGroups(chats.filter(c => c.isGroup).map(c => ({
            phone: c.id?.replace(/@.*$/, "") || c.id,
            name: c.name,
            isGroup: true,
            chatId: c.id,
          })));
        } catch {}
      }
      setLoadingGrp(false);
    })();
  }, [session]);

  const filteredContacts = contacts.filter(c =>
    !search ||
    (c.name||"").toLowerCase().includes(search.toLowerCase()) ||
    getPhone(c).includes(search)
  );

  const filteredGroups = groups.filter(g =>
    !search ||
    (g.name||"").toLowerCase().includes(search.toLowerCase()) ||
    (g.phone||"").includes(search)
  );

  const filteredClientGroups = clientGroups.filter(g =>
    !search ||
    (g.name||"").toLowerCase().includes(search.toLowerCase())
  );

  const applyTemplate = (t) => setMessage(t.text);

  const toggleAllContacts = () => {
    const ids = filteredContacts.filter(hasPhone).map(c => c.id);
    setSelected(selected.size === ids.length ? new Set() : new Set(ids));
  };

  const toggleAllGroups = () => {
    const ids = filteredGroups.map(g => g.phone);
    setSelGroups(selGroups.size === ids.length ? new Set() : new Set(ids));
  };

  const toggleAllClientGroups = () => {
    const ids = filteredClientGroups.map(g => g.id);
    setSelClientGroups(selClientGroups.size === ids.length ? new Set() : new Set(ids));
  };

  const resetBroadcast = () => {
    setStarted(false); setResults([]); setProgress(0); setSending(false);
  };

  // حساب عدد أعضاء مجموعات العملاء المحددة (بدون تكرار)
  const clientGroupMemberPhones = new Set();
  for (const gid of selClientGroups) {
    (clientGroupMembers[gid] || []).forEach(m => {
      if (m.phone) clientGroupMemberPhones.add(normalizePhone(m.phone));
    });
  }
  const totalSelected = selected.size + selGroups.size + (selClientGroups.size > 0 ? clientGroupMemberPhones.size : 0);

  const startBroadcast = async () => {
    if (totalSelected === 0 || (!message.trim() && files.length === 0 && selectedDriveFiles.length === 0)) {
      toast.error("اختر مستلمين واكتب رسالة أو أرفق ملفات"); return;
    }

    // جمّع الأهداف: جهات اتصال + مجموعات عملاء + جروبات واتساب
    const contactTargets = contacts.filter(c => selected.has(c.id) && hasPhone(c)).map(c => ({
      type: "contact", name: c.name, phone: normalizePhone(getPhone(c)),
      recipientType, recipientId: c.id, recipientName: c.name,
    }));

    // أعضاء مجموعات العملاء — كجهات اتصال فردية (بدون تكرار)
    const cgMemberTargets = [];
    const cgPhonesSeen = new Set(contactTargets.map(t => t.phone));
    for (const gid of selClientGroups) {
      (clientGroupMembers[gid] || []).forEach(m => {
        if (m.phone) {
          const phone = normalizePhone(m.phone);
          if (!cgPhonesSeen.has(phone)) {
            cgPhonesSeen.add(phone);
            cgMemberTargets.push({
              type: "contact", name: m.name, phone,
              recipientType, recipientId: m.id, recipientName: m.name,
            });
          }
        }
      });
    }

    const groupTargets = groups.filter(g => selGroups.has(g.phone)).map(g => ({
      type: "group", name: g.name, phone: g.phone,
      chatId: g.chatId || (g.phone + "@g.us"), isGroup: true,
    }));
    const allTargets = [...contactTargets, ...cgMemberTargets, ...groupTargets];

    const initialResults = allTargets.map(t => ({ contact: { name: t.name, phone: t.phone }, status: "pending", error: "" }));
    setResults(initialResults);
    setStarted(true);
    setSending(true);

    for (let i = 0; i < allTargets.length; i++) {
      const t = allTargets[i];
      const msg = personalize(message, { name: t.name });
      try {
        if (files.length > 0 || selectedDriveFiles.length > 0) {
          // 1. إرسال النص العام كرسالة منفصلة أولاً (قبل الملفات)
          if (msg.trim()) {
            const meta = t.isGroup ? { chatId: t.chatId, isGroup: true } : {
              recipientType: t.recipientType, recipientId: t.recipientId, recipientName: t.recipientName,
            };
            await waApi.sendMessage(session, t.phone, msg, meta);
            await new Promise(r => setTimeout(r, 2000));
          }
          // 2. إرسال الملفات المحلية
          for (let fi = 0; fi < files.length; fi++) {
            const fd = new FormData();
            fd.append("phone", t.phone);
            fd.append("file", files[fi]);
            if (t.isGroup && t.chatId) {
              fd.append("chatId", t.chatId);
              fd.append("isGroup", "true");
            }
            fd.append("recipientType", t.recipientType || "");
            fd.append("recipientName", t.name || "");
            await waApi.sendFile(session, fd);
            if (fi < files.length - 1) await new Promise(r => setTimeout(r, 3000));
          }
          // 3. إرسال ملفات Drive — اسم الملف من Drive + caption عربي تعريفي
          for (let di = 0; di < selectedDriveFiles.length; di++) {
            const df = selectedDriveFiles[di];
            // النص التعريفي العربي يكون caption تحت الملف
            const arabicCaption = df.doc_type === "تقرير_توفر"
              ? `📊 ${df.title || "تقرير توفر الوحدات"}`
              : `📎 ${df.doc_type || df.title || "ملف ترويجي"}`;
            if (df.drive_file_id) {
              await waApi.sendDriveFile(session, {
                phone: t.phone,
                chatId: t.isGroup ? (t.chatId || (t.phone + "@g.us")) : undefined,
                isGroup: t.isGroup || false,
                driveFileId: df.drive_file_id,
                caption: arabicCaption,
                recipientType: t.recipientType || "",
                recipientName: t.name || "",
              });
            } else if (df.drive_web_link) {
              const linkMsg = `${arabicCaption}\n${df.drive_web_link}`;
              const meta = t.isGroup ? { chatId: t.chatId, isGroup: true } : {};
              await waApi.sendMessage(session, t.phone, linkMsg, meta);
            }
            if (di < selectedDriveFiles.length - 1) await new Promise(r => setTimeout(r, 3000));
          }
        } else {
          const meta = t.isGroup ? { chatId: t.chatId, isGroup: true } : {
            recipientType: t.recipientType, recipientId: t.recipientId, recipientName: t.recipientName,
          };
          await waApi.sendMessage(session, t.phone, msg, meta);
        }
        setResults(prev => { const n=[...prev]; n[i]={...n[i],status:"sent"}; return n; });
      } catch (e) {
        setResults(prev => { const n=[...prev]; n[i]={...n[i],status:"failed",error:e.response?.data?.error||e.message}; return n; });
      }
      setProgress(i + 1);
      if (i < allTargets.length - 1) {
        const delay = 7000 + Math.floor(Math.random() * 5000); // 7-12 ثانية عشوائي لتجنب حظر واتساب
        await new Promise(r => setTimeout(r, delay));
      }
    }

    setSending(false);
    refreshDailyStatus();
  };

  const sentCount = results.filter(r => r.status === "sent").length;

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {!started ? (
        <div style={{ flex:1, overflow:"auto", padding:20 }}>
          {/* Message */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>
              ✍️ نص الرسالة <span style={{ fontWeight:400, color:"#94a3b8" }}>({"{الاسم}"} = اسم تلقائي)</span>
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
              placeholder="اكتب رسالتك هنا..."
              style={{
                width:"100%", boxSizing:"border-box", padding:"12px 14px",
                border:"2px solid #e2e8f0", borderRadius:10, fontFamily:"inherit", fontSize:14, resize:"vertical"
              }}/>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
              {templates.map((t,i) => (
                <button key={i} onClick={() => applyTemplate(t)} style={{
                  padding:"5px 12px", background: message===t.text?"#dcfce7":"#f1f5f9",
                  border: message===t.text?"1px solid #86efac":"1px solid #e2e8f0",
                  borderRadius:16, cursor:"pointer", fontSize:12, fontFamily:"inherit"
                }}>{t.icon} {t.label}</button>
              ))}
            </div>
          </div>

          {/* Files — رفع ملفات متعددة */}
          <div style={{ marginBottom:16 }}>
            {files.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:8 }}>
                {files.map((f, idx) => (
                  <div key={idx} style={{ display:"flex", alignItems:"center", gap:10, background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"8px 14px" }}>
                    <FiFile size={15} color="#16a34a"/>
                    <span style={{ flex:1, fontSize:13, color:"#16a34a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                    <span style={{ fontSize:11, color:"#94a3b8", flexShrink:0 }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(idx)} style={{ background:"none", border:"none", cursor:"pointer", color:"#dc2626", padding:2 }}><FiX size={15}/></button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => fileRef.current?.click()} style={{
              padding:"8px 16px", background:"#f8fafc", border:"2px dashed #cbd5e1",
              borderRadius:10, cursor:"pointer", fontSize:13, color:"#64748b",
              display:"flex", alignItems:"center", gap:8, width:"100%", justifyContent:"center"
            }}><FiPaperclip size={14}/> {files.length > 0 ? `إضافة ملفات أخرى (${files.length} مرفق)` : "إرفاق ملفات (اختياري)"}</button>
            {files.length > 1 && (
              <button onClick={() => setFiles([])} style={{
                marginTop:6, padding:"4px 12px", background:"none", border:"1px solid #fca5a5",
                borderRadius:8, cursor:"pointer", fontSize:11, color:"#dc2626", fontFamily:"inherit"
              }}>🗑️ حذف جميع المرفقات</button>
            )}
            <input ref={fileRef} type="file" multiple style={{display:"none"}}
              onChange={e => { addFiles(e.target.files); e.target.value = ""; }}/>
          </div>

          {/* ═══ Project Attachments — مرفقات المشروع ═══ */}
          <div style={{ marginBottom:16, border:"1px solid #e0e7ff", borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"8px 14px", background:"#f0f0ff", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#4338ca" }}>🏗️ مرفقات من المشروع</span>
              <select value={selectedProject || ""} onChange={e => { setSelectedProject(e.target.value || null); loadProjectDocs(e.target.value); }}
                style={{ flex:1, padding:"5px 8px", border:"1px solid #c7d2fe", borderRadius:6, fontSize:12, fontFamily:"inherit", background:"#fff" }}>
                <option value="">— اختر مشروع —</option>
                {projectsList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {selectedProject && (
              <div style={{ padding:10 }}>
                {loadingProjectDocs ? (
                  <div style={{ textAlign:"center", padding:10, color:"#94a3b8", fontSize:12 }}>⏳ جاري التحميل...</div>
                ) : projectDocs.length === 0 ? (
                  <div style={{ textAlign:"center", padding:10, color:"#94a3b8", fontSize:12 }}>لا توجد ملفات ترويجية أو تقارير</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    {projectDocs.map(doc => {
                      const isSelected = selectedDriveFiles.some(f => f.id === doc.id);
                      const isOffer = doc.doc_category === "offer";
                      const icon = isOffer ? "🎁" : doc.doc_type === "البروشور الرسمي" ? "📄" : doc.doc_type === "الفيديو الدعائي" ? "🎬" : doc.doc_type === "تقرير_توفر" ? "📊" : "📎";
                      const label = isOffer ? (doc.title || "عرض تسويقي")
                        : doc.doc_type === "تقرير_توفر" ? `تقرير توفر — ${doc.title || doc.created_at?.slice(0,10)}`
                        : (doc.doc_type || doc.title || "ملف");
                      const linkedModels = isOffer ? (doc.linked_unit_models || "").split("|").filter(Boolean) : [];
                      return (
                        <label key={doc.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", borderRadius:8, cursor:"pointer", fontSize:12,
                          background: isSelected ? (isOffer ? "#fffbeb" : "#eef2ff") : "transparent", border: isSelected ? (isOffer ? "1px solid #fcd34d" : "1px solid #c7d2fe") : "1px solid transparent" }}>
                          <input type="checkbox" checked={isSelected}
                            onChange={e => setSelectedDriveFiles(prev => e.target.checked
                              ? [...prev, { id: doc.id, title: doc.title || doc.doc_type, doc_type: doc.doc_type, doc_category: doc.doc_category, drive_file_id: doc.drive_file_id, drive_web_link: doc.drive_web_link }]
                              : prev.filter(f => f.id !== doc.id)
                            )} style={{ width:14, height:14, accentColor: isOffer ? "#f59e0b" : "#4338ca" }} />
                          <span style={{ flex: 1 }}>{icon} {label}</span>
                          {doc.doc_type === "تقرير_توفر" && <span style={{ fontSize:9, background:"#dbeafe", color:"#1d4ed8", padding:"1px 5px", borderRadius:4 }}>تقرير</span>}
                          {isOffer && <span style={{ fontSize:9, background:"#fef3c7", color:"#92400e", padding:"1px 5px", borderRadius:4, fontWeight:700 }}>عرض</span>}
                          {linkedModels.length > 0 && (
                            <span style={{ fontSize: 9, color: "#92400e" }}>🏠 {linkedModels.join("، ")}</span>
                          )}
                        </label>
                      );
                    })}
                    {selectedDriveFiles.length > 0 && (
                      <div style={{ fontSize:10, color:"#4338ca", fontWeight:700, padding:"4px 10px" }}>✅ {selectedDriveFiles.length} مرفق من المشروع</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Inner tabs: جهات اتصال / مجموعات / جروبات واتساب */}
          <div style={{ display:"flex", gap:0, marginBottom:12, borderRadius:10, overflow:"hidden", border:"1px solid #e2e8f0" }}>
            <button onClick={() => setMode("contacts")} style={{
              flex:1, padding:"10px", border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600,
              background: mode==="contacts" ? "#128C7E" : "#f8fafc",
              color: mode==="contacts" ? "#fff" : "#64748b",
            }}>
              <FiUsers size={13} style={{ marginLeft:4, verticalAlign:"middle" }}/>
              جهات اتصال ({contacts.filter(hasPhone).length})
              {selected.size > 0 && <span style={{ background:"#25D366", color:"#fff", padding:"1px 7px", borderRadius:10, fontSize:10, marginRight:4 }}>{selected.size}</span>}
            </button>
            <button onClick={() => setMode("clientGroups")} style={{
              flex:1, padding:"10px", border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600,
              background: mode==="clientGroups" ? "#f59e0b" : "#f8fafc",
              color: mode==="clientGroups" ? "#fff" : "#64748b",
              borderRight:"1px solid #e2e8f0", borderLeft:"1px solid #e2e8f0",
            }}>
              <FiUsers size={13} style={{ marginLeft:4, verticalAlign:"middle" }}/>
              {groupsLabel} ({clientGroups.length})
              {selClientGroups.size > 0 && <span style={{ background:"#fbbf24", color:"#78350f", padding:"1px 7px", borderRadius:10, fontSize:10, marginRight:4 }}>{selClientGroups.size}</span>}
            </button>
            <button onClick={() => setMode("waGroups")} style={{
              flex:1, padding:"10px", border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600,
              background: mode==="waGroups" ? "#3b82f6" : "#f8fafc",
              color: mode==="waGroups" ? "#fff" : "#64748b",
            }}>
              <FaWhatsapp size={13} style={{ marginLeft:4, verticalAlign:"middle" }}/>
              جروبات واتساب ({groups.length})
              {selGroups.size > 0 && <span style={{ background:"#60a5fa", color:"#fff", padding:"1px 7px", borderRadius:10, fontSize:10, marginRight:4 }}>{selGroups.size}</span>}
            </button>
          </div>

          {/* Contacts list */}
          {mode === "contacts" && (
            <div style={{ border:"1px solid #e2e8f0", borderRadius:12, overflow:"hidden" }}>
              <div style={{ padding:"9px 14px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10 }}>
                <FiSearch size={14} color="#94a3b8"/>
                <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ border:"none", background:"transparent", outline:"none", fontFamily:"inherit", fontSize:14, flex:1 }}/>
                <button onClick={toggleAllContacts} style={{ fontSize:12, background:"none", border:"1px solid #cbd5e1", borderRadius:6, padding:"4px 10px", cursor:"pointer" }}>
                  {selected.size === filteredContacts.filter(hasPhone).length ? "إلغاء الكل" : "تحديد الكل"}
                </button>
                <span style={{ fontSize:12, background:"#e2e8f0", padding:"3px 10px", borderRadius:12, fontWeight:600 }}>
                  {selected.size} محدد
                </span>
              </div>
              <div style={{ maxHeight:300, overflowY:"auto" }}>
                {filteredContacts.map(c => (
                  <label key={c.id} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
                    borderBottom:"1px solid #f1f5f9", cursor: hasPhone(c)?"pointer":"not-allowed",
                    opacity: hasPhone(c)?1:0.4,
                    background: selected.has(c.id)?"#f0fdf4":"transparent"
                  }}>
                    <input type="checkbox" checked={selected.has(c.id)} disabled={!hasPhone(c)}
                      onChange={() => setSelected(prev => {
                        const n = new Set(prev);
                        n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n;
                      })}
                      style={{ width:15, height:15, accentColor:"#25D366" }}
                    />
                    <div style={{
                      width:32, height:32, borderRadius:"50%", flexShrink:0,
                      background: selected.has(c.id)?"#25D366":"#e2e8f0",
                      color: selected.has(c.id)?"#fff":"#64748b",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontWeight:700, fontSize:13
                    }}>{(c.name||"?")[0]}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{c.name}</div>
                      <div style={{ fontSize:11, color:"#94a3b8", direction:"ltr", textAlign:"right" }}>
                        {hasPhone(c) ? displayPhone(getPhone(c)) : "⚠️ لا يوجد رقم"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Client Groups list — مجموعات العملاء */}
          {mode === "clientGroups" && (
            <div style={{ border:"1px solid #fde68a", borderRadius:12, overflow:"hidden" }}>
              <div style={{ padding:"9px 14px", background:"#fffbeb", borderBottom:"1px solid #fde68a", display:"flex", alignItems:"center", gap:10 }}>
                <FiSearch size={14} color="#94a3b8"/>
                <input type="text" placeholder={`بحث في ${groupsLabel}...`} value={search} onChange={e => setSearch(e.target.value)}
                  style={{ border:"none", background:"transparent", outline:"none", fontFamily:"inherit", fontSize:14, flex:1 }}/>
                <button onClick={toggleAllClientGroups} style={{ fontSize:12, background:"none", border:"1px solid #fbbf24", borderRadius:6, padding:"4px 10px", cursor:"pointer", color:"#b45309" }}>
                  {selClientGroups.size === filteredClientGroups.length ? "إلغاء الكل" : "تحديد الكل"}
                </button>
                <span style={{ fontSize:12, background:"#fef3c7", color:"#92400e", padding:"3px 10px", borderRadius:12, fontWeight:600 }}>
                  {selClientGroups.size} محدد
                </span>
              </div>
              <div style={{ maxHeight:300, overflowY:"auto" }}>
                {loadingCG ? (
                  <div style={{ padding:20, textAlign:"center", color:"#94a3b8", fontSize:13 }}>جاري تحميل المجموعات...</div>
                ) : filteredClientGroups.length === 0 ? (
                  <div style={{ padding:20, textAlign:"center", color:"#94a3b8", fontSize:13 }}>لا توجد {groupsLabel}</div>
                ) : filteredClientGroups.map(g => (
                  <label key={g.id} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
                    borderBottom:"1px solid #f1f5f9", cursor:"pointer",
                    background: selClientGroups.has(g.id)?"#fffbeb":"transparent"
                  }}>
                    <input type="checkbox" checked={selClientGroups.has(g.id)}
                      onChange={() => setSelClientGroups(prev => {
                        const n = new Set(prev);
                        n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n;
                      })}
                      style={{ width:15, height:15, accentColor:"#f59e0b" }}
                    />
                    <div style={{
                      width:32, height:32, borderRadius:"50%", flexShrink:0,
                      background: selClientGroups.has(g.id) ? (g.color||"#f59e0b") : "#fef3c7",
                      color: selClientGroups.has(g.id)?"#fff":"#92400e",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontWeight:700, fontSize:12
                    }}><FiUsers size={14}/></div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{g.name}</div>
                      <div style={{ fontSize:11, color:"#94a3b8" }}>
                        {g.member_count} {recipientType === "marketer" ? "مسوق" : "عميل"}
                        {selClientGroups.has(g.id) && clientGroupMembers[g.id] && (
                          <span style={{ marginRight:6, color:"#b45309" }}>
                            — {clientGroupMembers[g.id].filter(m => m.phone).length} رقم
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {selClientGroups.size > 0 && (
                <div style={{ padding:"8px 14px", background:"#fef3c7", borderTop:"1px solid #fde68a", fontSize:12, color:"#92400e", fontWeight:600, textAlign:"center" }}>
                  سيتم إرسال الرسالة لأعضاء المجموعات المحددة كجهات اتصال فردية ({clientGroupMemberPhones.size} رقم فريد)
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Groups list — جروبات واتساب */}
          {mode === "waGroups" && (
            <div style={{ border:"1px solid #dbeafe", borderRadius:12, overflow:"hidden" }}>
              <div style={{ padding:"9px 14px", background:"#eff6ff", borderBottom:"1px solid #dbeafe", display:"flex", alignItems:"center", gap:10 }}>
                <FiSearch size={14} color="#94a3b8"/>
                <input type="text" placeholder="بحث في جروبات واتساب..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ border:"none", background:"transparent", outline:"none", fontFamily:"inherit", fontSize:14, flex:1 }}/>
                <button onClick={toggleAllGroups} style={{ fontSize:12, background:"none", border:"1px solid #93c5fd", borderRadius:6, padding:"4px 10px", cursor:"pointer", color:"#3b82f6" }}>
                  {selGroups.size === filteredGroups.length ? "إلغاء الكل" : "تحديد الكل"}
                </button>
                <span style={{ fontSize:12, background:"#dbeafe", color:"#1d4ed8", padding:"3px 10px", borderRadius:12, fontWeight:600 }}>
                  {selGroups.size} محدد
                </span>
              </div>
              <div style={{ maxHeight:300, overflowY:"auto" }}>
                {loadingGrp ? (
                  <div style={{ padding:20, textAlign:"center", color:"#94a3b8", fontSize:13 }}>جاري تحميل المجموعات...</div>
                ) : filteredGroups.length === 0 ? (
                  <div style={{ padding:20, textAlign:"center", color:"#94a3b8", fontSize:13 }}>لا توجد جروبات واتساب</div>
                ) : filteredGroups.map(g => (
                  <label key={g.phone} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
                    borderBottom:"1px solid #f1f5f9", cursor:"pointer",
                    background: selGroups.has(g.phone)?"#eff6ff":"transparent"
                  }}>
                    <input type="checkbox" checked={selGroups.has(g.phone)}
                      onChange={() => setSelGroups(prev => {
                        const n = new Set(prev);
                        n.has(g.phone) ? n.delete(g.phone) : n.add(g.phone); return n;
                      })}
                      style={{ width:15, height:15, accentColor:"#3b82f6" }}
                    />
                    <div style={{
                      width:32, height:32, borderRadius:"50%", flexShrink:0,
                      background: selGroups.has(g.phone)?"#3b82f6":"#dbeafe",
                      color: selGroups.has(g.phone)?"#fff":"#3b82f6",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontWeight:700, fontSize:12
                    }}><FaWhatsapp size={14}/></div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{g.name}</div>
                      <div style={{ fontSize:11, color:"#94a3b8" }}>جروب واتساب</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Progress view */
        <div style={{ flex:1, overflow:"auto", padding:20 }}>
          <div style={{ marginBottom:18 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:14, fontWeight:600, marginBottom:6 }}>
              <span>التقدم: {progress} / {results.length}</span>
              {sending
                ? <span style={{ color:"#d97706" }}>⏳ جاري الإرسال...</span>
                : <span style={{ color:"#16a34a" }}>✅ اكتمل — تم: {sentCount}</span>
              }
            </div>
            <div style={{ background:"#e2e8f0", borderRadius:8, height:8, overflow:"hidden" }}>
              <div style={{
                height:"100%", borderRadius:8,
                background:"linear-gradient(90deg,#25D366,#128C7E)",
                width: results.length ? `${(results.filter(r=>r.status!=="pending").length / results.length)*100}%` : "0%",
                transition:"width 0.3s"
              }}/>
            </div>
          </div>
          <div style={{ border:"1px solid #e2e8f0", borderRadius:12, overflow:"hidden" }}>
            {results.map((item, idx) => (
              <div key={idx} style={{
                display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
                borderBottom: idx < results.length-1 ? "1px solid #f1f5f9" : "none",
              }}>
                <div style={{
                  width:26, height:26, borderRadius:"50%", flexShrink:0,
                  background: item.status==="sent"?"#dcfce7":item.status==="failed"?"#fee2e2":"#f8fafc",
                  display:"flex", alignItems:"center", justifyContent:"center"
                }}>
                  {item.status==="sent"    ? <FiCheck size={13} color="#16a34a"/> :
                   item.status==="failed"  ? <FiAlertCircle size={13} color="#dc2626"/> :
                   item.status==="pending" ? <span className="wa-spin" style={{fontSize:14}}>↻</span> : "○"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:500 }}>{item.contact.name}</div>
                  {item.error && <div style={{ fontSize:11, color:"#ef4444" }}>{item.error}</div>}
                </div>
                <span style={{ fontSize:11, color:"#94a3b8" }}>
                  {item.status==="sent"?"✓ تم":item.status==="failed"?"✗ فشل":"..."}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ padding:"14px 20px", borderTop:"1px solid #e2e8f0", flexShrink:0 }}>
        {/* عداد الرسائل اليومي */}
        <div style={{
          display:"flex", justifyContent:"space-between", alignItems:"center",
          padding:"8px 12px", marginBottom:10, borderRadius:8,
          background: dailyStatus.blocked ? "#fef2f2" : dailyStatus.remaining <= 10 ? "#fefce8" : "#f0fdf4",
          border: `1px solid ${dailyStatus.blocked ? "#fca5a5" : dailyStatus.remaining <= 10 ? "#fde68a" : "#bbf7d0"}`
        }}>
          <div style={{ fontSize:11, fontWeight:600, color: dailyStatus.blocked ? "#dc2626" : dailyStatus.remaining <= 10 ? "#d97706" : "#16a34a", display:"flex", alignItems:"center", gap:6 }}>
            {editingLimit ? (
              <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                <span>📊 رسائل اليوم: {dailyStatus.count} /</span>
                <input type="number" min={1} value={limitInput} autoFocus
                  onChange={e => setLimitInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveDailyLimit(); if (e.key === "Escape") setEditingLimit(false); }}
                  style={{ width:64, padding:"2px 6px", border:"1px solid #cbd5e1", borderRadius:6, fontSize:12, fontFamily:"inherit" }} />
                <button onClick={saveDailyLimit} title="حفظ" style={{ border:"none", background:"#16a34a", color:"#fff", borderRadius:6, padding:"2px 8px", cursor:"pointer", fontSize:12 }}>✓</button>
                <button onClick={() => setEditingLimit(false)} title="إلغاء" style={{ border:"none", background:"#e2e8f0", color:"#475569", borderRadius:6, padding:"2px 8px", cursor:"pointer", fontSize:12 }}>✕</button>
              </span>
            ) : (
              <>
                <span>{dailyStatus.blocked ? "⛔ تم الوصول للحد اليومي" : `📊 رسائل اليوم: ${dailyStatus.count} / ${dailyStatus.limit}`}</span>
                {canEditLimit && (
                  <button onClick={() => { setLimitInput(String(dailyStatus.limit)); setEditingLimit(true); }}
                    title="تعديل الحد اليومي"
                    style={{ border:"none", background:"transparent", cursor:"pointer", color:"#64748b", fontSize:12, padding:0 }}>✏️</button>
                )}
              </>
            )}
          </div>
          <div style={{ fontSize:10, color:"#94a3b8" }}>
            المتبقي: <strong style={{ color: dailyStatus.remaining <= 10 ? "#dc2626" : "#166534" }}>{dailyStatus.remaining}</strong>
            <span style={{ marginRight:6, fontSize:9 }}>⏱️ تأخير 7-12 ث/رسالة</span>
          </div>
        </div>
        {!started ? (
          <button onClick={startBroadcast}
            disabled={totalSelected===0 || (!message.trim() && files.length===0 && selectedDriveFiles.length===0) || dailyStatus.blocked}
            style={{
              width:"100%", padding:"13px",
              background: dailyStatus.blocked ? "#dc2626" : (totalSelected>0 && (message.trim()||files.length>0||selectedDriveFiles.length>0)) ? "#128C7E" : "#94a3b8",
              border:"none", borderRadius:12, cursor: dailyStatus.blocked ? "not-allowed" : "pointer",
              color:"#fff", fontFamily:"inherit", fontSize:15, fontWeight:700,
              display:"flex", alignItems:"center", justifyContent:"center", gap:10
            }}
          >
            {dailyStatus.blocked ? "⛔ تم الوصول للحد اليومي — حماية من الحظر" : (<>
            <FiRadio size={17}/> إرسال لـ {totalSelected} (
              {[
                selected.size > 0 ? `${selected.size} جهة` : "",
                selClientGroups.size > 0 ? `${clientGroupMemberPhones.size} من مجموعات` : "",
                selGroups.size > 0 ? `${selGroups.size} جروب` : "",
              ].filter(Boolean).join(" + ")}
            )</>)}
          </button>
        ) : !sending && (
          <button onClick={resetBroadcast} style={{
            width:"100%", padding:"12px",
            background:"#128C7E", border:"none", borderRadius:12, cursor:"pointer",
            fontFamily:"inherit", fontSize:14, fontWeight:600, color:"#fff"
          }}>بث جديد</button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ContactList
// ═══════════════════════════════════════════════════════════════
function ContactList({ session, recipientType, contacts, templates, onSelect, selectedId }) {
  const [search,    setSearch]    = useState("");
  const [previews,  setPreviews]  = useState({});

  const loadPreviews = useCallback(async () => {
    try {
      const res = await waApi.getChats(session);
      const chats = res.data?.chats || res.data || [];
      const map = {};
      chats.forEach(chat => {
        const uid = chat.id?.user || chat.id;
        if (uid) map[uid] = chat;
      });
      setPreviews(map);
    } catch { /* silent */ }
  }, [session]);

  useEffect(() => {
    loadPreviews();
    const iv = setInterval(loadPreviews, 10000);
    return () => clearInterval(iv);
  }, [loadPreviews]);

  const filtered = contacts.filter(c =>
    !search ||
    (c.name||"").toLowerCase().includes(search.toLowerCase()) ||
    getPhone(c).includes(search)
  );

  const getPhoneNum = (c) => {
    let p = getPhone(c).replace(/[\s\-().+]/g, "");
    if (p.startsWith("00")) p = p.slice(2);
    if (p.startsWith("0"))  p = "966" + p.slice(1);
    if (!p.startsWith("966")) p = "966" + p;
    return p;
  };

  const fmtTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts*1000), now = new Date();
    return d.toDateString() === now.toDateString()
      ? d.toLocaleTimeString("ar-SA", { hour:"2-digit", minute:"2-digit" })
      : d.toLocaleDateString("ar-SA", { month:"short", day:"numeric" });
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#fff" }}>
      {/* Search */}
      <div style={{ padding:"10px 12px 6px", borderBottom:"1px solid #f1f5f9", flexShrink:0 }}>
        <div style={{
          display:"flex", alignItems:"center", gap:8,
          background:"#f1f5f9", borderRadius:20, padding:"7px 14px"
        }}>
          <FiSearch size={14} color="#94a3b8"/>
          <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex:1, border:"none", background:"transparent", fontFamily:"inherit", fontSize:14, outline:"none" }}/>
          {search && (
            <button onClick={() => setSearch("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8" }}>
              <FiX size={13}/>
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div style={{ flex:1, overflowY:"auto" }}>
        {filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:40, color:"#94a3b8", fontSize:14 }}>لا توجد نتائج</div>
        )}
        {filtered.map(c => {
          const preview = previews[getPhoneNum(c)] || null;
          const hp = hasPhone(c);
          const active = c.id === selectedId;
          return (
            <div key={c.id} onClick={() => hp && onSelect(c)} style={{
              padding:"11px 14px", borderBottom:"1px solid #f8fafc",
              display:"flex", alignItems:"center", gap:11,
              cursor: hp ? "pointer" : "not-allowed",
              opacity: hp ? 1 : 0.4,
              background: active ? "#e7f5ef" : "transparent",
              borderRight: active ? "3px solid #25D366" : "3px solid transparent",
              transition:"background 0.15s"
            }}>
              <div style={{ position:"relative", flexShrink:0 }}>
                <div style={{
                  width:44, height:44, borderRadius:"50%",
                  background: active ? "#25D366" : "#e2e8f0",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontWeight:700, fontSize:16,
                  color: active ? "#fff" : "#64748b"
                }}>{(c.name||"?")[0]}</div>
                {preview?.unreadCount > 0 && (
                  <div style={{
                    position:"absolute", top:-2, left:-2,
                    background:"#ef4444", color:"#fff",
                    width:17, height:17, borderRadius:"50%",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:9, fontWeight:700
                  }}>{preview.unreadCount > 9 ? "9+" : preview.unreadCount}</div>
                )}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                  <span style={{ fontWeight:600, fontSize:14, color:"#1e293b" }}>{c.name}</span>
                  {preview && <span style={{ fontSize:11, color:"#94a3b8", flexShrink:0 }}>{fmtTime(preview.timestamp)}</span>}
                </div>
                <div style={{ fontSize:12, color:"#94a3b8", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {preview?.lastMessage?.body || (hp ? displayPhone(getPhone(c)) : "⚠️ لا يوجد رقم")}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SessionTab
// ═══════════════════════════════════════════════════════════════
function SessionTab({ session, label, recipientType, contacts, templates }) {
  const [selected, setSelected] = useState(null);
  const [status,   setStatus]   = useState("loading");
  const [groupParticipants, setGroupParticipants] = useState(null); // { name, participants[], loading, error }

  const fetchGroupMembers = async (group) => {
    setGroupParticipants({ name: group.name, chatId: group.chatId || group.phone, participants: [], loading: true, error: null });
    try {
      const res = await waApi.getGroupParticipants(session, group.chatId || group.phone);
      const data = res.data || {};
      setGroupParticipants({
        name: data.groupName || group.name,
        chatId: group.chatId || group.phone,
        participants: data.participants || [],
        participantCount: data.participantCount || 0,
        loading: false,
        error: null,
      });
    } catch (e) {
      setGroupParticipants(prev => prev ? { ...prev, loading: false, error: "فشل سحب الأعضاء — تأكد من دعم WA Server لهذه الميزة" } : null);
    }
  };

  const checkStatus = useCallback(async () => {
    try {
      const res = await waApi.getStatus(session);
      const d = res.data;
      setStatus(d.status === "CONNECTED" || d.connected ? "connected" : "disconnected");
    } catch {
      setStatus("disconnected");
    }
  }, [session]);

  useEffect(() => {
    checkStatus();
    const iv = setInterval(checkStatus, 15000);
    return () => clearInterval(iv);
  }, [checkStatus]);

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      {/* Split */}
      <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
        <div style={{
          width: selected ? 300 : "100%",
          maxWidth: selected ? 340 : "100%",
          borderLeft:"1px solid #e2e8f0", flexShrink:0
        }}>
          <ContactList
            session={session}
            recipientType={recipientType}
            contacts={contacts}
            templates={templates}
            onSelect={setSelected}
            selectedId={selected?.id}
          />
        </div>

        {selected ? (
          <div style={{ flex:1, overflow:"hidden" }}>
            <ChatPanel
              session={session}
              contact={selected}
              recipientType={recipientType}
              templates={templates}
              onBack={() => setSelected(null)}
            />
          </div>
        ) : (
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:"#f0f2f5" }}>
            <div style={{ background:"#fff", borderRadius:24, padding:"40px 50px", textAlign:"center", boxShadow:"0 4px 24px rgba(0,0,0,0.08)" }}>
              <FaWhatsapp size={56} color="#25D366" style={{ marginBottom:14, opacity:0.7 }}/>
              <div style={{ fontSize:17, fontWeight:600, color:"#374151", marginBottom:6 }}>مركز تواصل رائل</div>
              <div style={{ fontSize:14, color:"#94a3b8" }}>اختر جهة اتصال من القائمة لبدء المحادثة</div>
            </div>
          </div>
        )}
      </div>

      {/* ═══ Group Participants Modal ═══ */}
      {groupParticipants && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setGroupParticipants(null)}>
          <div dir="rtl" onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, width: 480, maxHeight: "80vh", overflow: "auto", padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#3b82f6" }}>👥 أعضاء الجروب</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>💬 {groupParticipants.name}</div>
              </div>
              <button onClick={() => setGroupParticipants(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>

            {groupParticipants.loading ? (
              <div style={{ textAlign: "center", padding: 30, color: "#64748b", fontSize: 13 }}>⏳ جاري سحب أعضاء الجروب من واتساب...</div>
            ) : groupParticipants.error ? (
              <div style={{ padding: 16, background: "#fef2f2", borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>⚠️ {groupParticipants.error}</div>
                <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>قد يحتاج WA Server تحديث لدعم سحب metadata الجروبات</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, marginBottom: 10, background: "#f0fdf4", padding: "8px 12px", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>✅ {groupParticipants.participants.length} عضو</span>
                </div>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, maxHeight: 400, overflowY: "auto" }}>
                  {groupParticipants.participants.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 24, color: "#94a3b8", fontSize: 13 }}>لم يتم العثور على أعضاء</div>
                  ) : groupParticipants.participants.map((p, i) => (
                    <div key={p.phone + i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: i < groupParticipants.participants.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: p.isAdmin ? "#fef3c7" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: p.isAdmin ? "#d97706" : "#64748b", flexShrink: 0 }}>
                        {p.isAdmin ? "⭐" : (p.name || "?")[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {p.name || "غير معروف"}
                          {p.isAdmin && <span style={{ fontSize: 9, color: "#f59e0b", fontWeight: 700, marginRight: 6, background: "#fef3c7", padding: "1px 5px", borderRadius: 4 }}>مشرف</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b", direction: "ltr", fontFamily: "monospace" }}>+{p.phone}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 8 }}>
              {!groupParticipants.loading && !groupParticipants.error && groupParticipants.participants.length > 0 && (
                <button onClick={async () => {
                  try {
                    const res = await prospectsApi.bulkFromGroup({
                      members: groupParticipants.participants.map(p => ({ phone: p.phone, name: p.name || "" })),
                      groupName: groupParticipants.name || "جروب واتساب"
                    });
                    const d = res.data;
                    toast.success(`تم السحب ✅ — جديد: ${d.added} | محدّث: ${d.updated} | تخطي: ${d.skipped}`);
                  } catch (err) {
                    toast.error("فشل سحب الأعضاء للمحتملين");
                  }
                }}
                  style={{ padding: "8px 20px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                  📥 سحب الكل إلى المحتملين
                </button>
              )}
              <button onClick={() => setGroupParticipants(null)}
                style={{ padding: "8px 28px", background: "#f1f5f9", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// InboxTab — متابعة المحادثات (مثل واتساب ويب)
// المزامنة تتم دورياً في الباكند (كل 5 دقائق)
// عند الفتح: يقرأ من DB فقط (سريع)
// زر "مزامنة وتحديث" للسحب اليدوي عند الحاجة
// ═══════════════════════════════════════════════════════════════
function InboxTab({ session, personal = false }) { // v3.4.343 — personal: زر «محادثة جديدة»
  const [chats,      setChats]      = useState([]);
  const [selected,   setSelected]   = useState(null); // {phone, name}
  const [messages,   setMessages]   = useState([]);
  const [text,       setText]       = useState("");
  const [file,       setFile]       = useState(null);
  const [sending,    setSending]    = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMsgs,  setLoadingMsgs]  = useState(false);
  const [search,     setSearch]     = useState("");
  const [syncing,    setSyncing]    = useState(false);
  const [syncInfo,   setSyncInfo]   = useState(null);
  const [crawling,   setCrawling]   = useState(false);
  const [crawlProgress, setCrawlProgress] = useState(null);
  const crawlPollRef = useRef(null);
  const endRef   = useRef(null);
  const pollRef  = useRef(null);
  const fileRef  = useRef(null);
  const msgContainerRef = useRef(null);
  const [groupParticipants, setGroupParticipants] = useState(null);

  const fetchGroupMembers = async (group) => {
    setGroupParticipants({ name: group.name, chatId: group.chatId || group.phone, participants: [], loading: true, error: null });
    try {
      const res = await waApi.getGroupParticipants(session, group.chatId || group.phone);
      const data = res.data || {};
      setGroupParticipants({
        name: data.groupName || group.name,
        chatId: group.chatId || group.phone,
        participants: data.participants || [],
        participantCount: data.participantCount || 0,
        loading: false, error: null,
      });
    } catch {
      setGroupParticipants(prev => prev ? { ...prev, loading: false, error: "فشل سحب الأعضاء — تأكد من دعم WA Server لهذه الميزة" } : null);
    }
  };

  // ─── جلب القائمة من DB فقط (بدون sync) ──────────────────────
  const loadChats = useCallback(async () => {
    try {
      const res = await waApi.getInbox(session);
      setChats(res.data?.chats || []);
      setSyncInfo(res.data?.sync || null);
    } catch { /* silent */ }
    setLoadingChats(false);
  }, [session]);

  // ─── مزامنة يدوية (زر المستخدم) ───────────────────────────
  const doManualSync = useCallback(async () => {
    setSyncing(true);
    try {
      await waApi.sync(session);
      await loadChats();
      toast.success("تمت المزامنة ✓");
    } catch (e) {
      await loadChats(); // حتى لو فشل الـ sync، حدّث القائمة
      toast.error("فشل المزامنة");
    }
    setSyncing(false);
  }, [session, loadChats]);

  // ─── سحب كامل (Full Crawl) ────────────────────────────────
  const startFullCrawl = useCallback(async () => {
    if (crawling) return;
    if (!window.confirm("سحب كامل يجلب كل الرسائل التاريخية من واتساب.\nقد يستغرق عدة دقائق.\n\nمتابعة؟")) return;
    setCrawling(true);
    setCrawlProgress({ running: true, messagesStored: 0, chatsProcessed: 0, totalChatsAvailable: 0 });
    try {
      await waApi.startFullCrawl(session);
      toast.success("بدأ السحب الكامل...");
      // ابدأ متابعة التقدم
      crawlPollRef.current = setInterval(async () => {
        try {
          const res = await waApi.getCrawlStatus(session);
          setCrawlProgress(res.data);
          if (!res.data?.running) {
            clearInterval(crawlPollRef.current);
            setCrawling(false);
            await loadChats();
            toast.success(`✅ اكتمل السحب — ${(res.data?.messagesStored || 0).toLocaleString("en-US")} رسالة`);
          }
        } catch { /* silent */ }
      }, 3000);
    } catch (e) {
      setCrawling(false);
      toast.error("فشل بدء السحب الكامل");
    }
  }, [session, crawling, loadChats]);

  // تنظيف poll عند unmount
  useEffect(() => {
    return () => { if (crawlPollRef.current) clearInterval(crawlPollRef.current); };
  }, []);

  // جلب القائمة عند فتح التاب (بدون sync — سريع)
  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // تحديث القائمة من DB كل 60 ثانية (بدون sync)
  useEffect(() => {
    const iv = setInterval(() => loadChats(), 60000);
    return () => clearInterval(iv);
  }, [loadChats]);

  const shouldScrollRef = useRef(true); // scroll فقط عند أول تحميل أو إرسال

  // ─── جلب رسائل محادثة من DB ───────────────────────────────
  const loadMessages = useCallback(async () => {
    if (!selected?.phone) return;
    try {
      const res = await waApi.getInboxMessages(session, selected.phone, 500);
      setMessages(res.data?.messages || []);
    } catch { /* silent */ }
    setLoadingMsgs(false);
  }, [session, selected]);

  useEffect(() => {
    if (!selected) return;
    setMessages([]); setLoadingMsgs(true);
    shouldScrollRef.current = true; // scroll عند تغيير المحادثة
    loadMessages();
    // تحديث الرسائل من DB كل 30 ثانية (بدون sync)
    pollRef.current = setInterval(() => {
      loadMessages(); // ما نسوي scroll عند الـ polling
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadMessages, selected, session]);

  // scroll لآخر رسالة — فقط عند أول تحميل أو إرسال
  useEffect(() => {
    if (messages.length > 0 && !loadingMsgs && shouldScrollRef.current) {
      shouldScrollRef.current = false;
      const scrollToEnd = () => {
        if (msgContainerRef.current) {
          msgContainerRef.current.scrollTop = msgContainerRef.current.scrollHeight;
        }
      };
      // محاولات متعددة
      setTimeout(scrollToEnd, 50);
      setTimeout(scrollToEnd, 200);
      setTimeout(scrollToEnd, 500);
    }
  }, [messages, loadingMsgs]);

  // ─── إرسال رسالة ───────────────────────────────────────────
  const send = async () => {
    if (sending || (!text.trim() && !file) || !selected) return;
    setSending(true);
    try {
      const isGrp = !!selected.isGroup;
      if (file) {
        const fd = new FormData();
        fd.append("phone", selected.phone);
        fd.append("file", file);
        if (text.trim()) fd.append("caption", text.trim());
        if (isGrp) {
          fd.append("chatId", selected.chatId || selected.phone);
          fd.append("isGroup", "true");
        }
        await waApi.sendFile(session, fd);
        toast.success("تم إرسال الملف ✓");
      } else {
        const optimistic = { id:`opt_${Date.now()}`, body:text.trim(), fromMe:true, timestamp:Math.floor(Date.now()/1000), ack:1 };
        setMessages(prev => [optimistic, ...prev]);
        const meta = isGrp ? { chatId: selected.chatId || selected.phone, isGroup: true } : {};
        await waApi.sendMessage(session, selected.phone, text.trim(), meta);
        toast.success("تم الإرسال ✓");
      }
      setText(""); setFile(null);
      // sync بعد الإرسال لتحديث DB
      setTimeout(async () => {
        try { await waApi.sync(session); } catch { /* */ }
        shouldScrollRef.current = true; // scroll بعد الإرسال
        loadMessages();
      }, 2000);
    } catch(e) { toast.error("فشل الإرسال: " + (e.response?.data?.error || e.message)); }
    setSending(false);
  };

  const fmtTime = (ts) => ts ? new Date(ts*1000).toLocaleTimeString("ar-SA",{hour:"2-digit",minute:"2-digit"}) : "";
  const fmtDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts*1000), now = new Date();
    if (d.toDateString() === now.toDateString()) return "اليوم";
    const yesterday = new Date(now); yesterday.setDate(now.getDate()-1);
    if (d.toDateString() === yesterday.toDateString()) return "أمس";
    return d.toLocaleDateString("ar-SA");
  };

  const filtered = chats.filter(c =>
    c.name.includes(search) || c.phone.includes(search) || c.lastMsg?.includes(search)
  );

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      {/* ── قائمة المحادثات ── */}
      <div style={{ width:320, minWidth:320, borderLeft:"1px solid #e2e8f0", display:"flex", flexDirection:"column", flexShrink:0, height:"100%", overflow:"hidden" }}>
        {/* شريط المزامنة */}
        {syncing && (
          <div style={{ padding:"6px 12px", background:"#fef3c7", borderBottom:"1px solid #fcd34d", display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#92400e" }}>
            <span className="wa-spin"><FiRefreshCw size={12}/></span>
            جاري مزامنة الرسائل...
          </div>
        )}
        {syncInfo && !syncing && (
          <div style={{ padding:"4px 12px", background:"#f0fdf4", borderBottom:"1px solid #bbf7d0", fontSize:11, color:"#16a34a", textAlign:"center" }}>
            ✓ {syncInfo.total_messages?.toLocaleString("en-US")} رسالة محفوظة — آخر تحديث {syncInfo.last_sync_at ? new Date(syncInfo.last_sync_at).toLocaleTimeString("ar-SA", {hour:"2-digit", minute:"2-digit"}) : "—"}
          </div>
        )}
        {/* بحث */}
        <div style={{ padding:"10px 12px", borderBottom:"1px solid #f1f5f9" }}>
          <div style={{ position:"relative" }}>
            <FiSearch size={14} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", color:"#94a3b8" }}/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="بحث في المحادثات..."
              style={{ width:"100%", padding:"8px 32px 8px 12px", border:"1px solid #e2e8f0", borderRadius:20, fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
            />
          </div>
          {personal && (/* v3.4.343 — بدء محادثة مع رقم غير موجود في القائمة */
            <button onClick={() => { const raw = window.prompt("رقم الجوال (مثال: 05xxxxxxxx أو 9665xxxxxxxx):", ""); if (!raw) return; let d = String(raw).replace(/\D/g, ""); if (d.startsWith("00")) d = d.slice(2); if (d.startsWith("0")) d = "966" + d.slice(1); else if (/^5\d{8}$/.test(d)) d = "966" + d; if (d.length < 11) return toast.error("رقم غير صالح"); const existing = chats.find(c => String(c.phone) === d); setSelected(existing || { phone: d, name: "+" + d, isNew: true }); }}
              style={{ marginTop:8, width:"100%", padding:"8px 10px", border:"1px dashed #25D366", borderRadius:20, background:"#f0fdf4", color:"#166534", fontSize:12.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>✏️ محادثة جديدة لرقم</button>
          )}
        </div>
        {/* قائمة */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {loadingChats ? (
            <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>
              <span className="wa-spin" style={{ display:"inline-block", marginBottom:8 }}><FiRefreshCw size={20}/></span>
              <br/>جاري سحب المحادثات...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>لا توجد محادثات</div>
          ) : filtered.map(c => (
            <div key={c.phone} onClick={() => setSelected(c)}
              style={{
                padding:"12px 14px", cursor:"pointer", borderBottom:"1px solid #f8fafc",
                background: selected?.phone === c.phone ? "#f0fdf4" : "transparent",
                display:"flex", alignItems:"center", gap:12,
                transition:"background 0.15s"
              }}>
              <div style={{
                width:42, height:42, borderRadius:"50%", flexShrink:0,
                background: c.isGroup ? "linear-gradient(135deg,#3b82f6,#1d4ed8)" : "linear-gradient(135deg,#25D366,#128C7E)",
                display:"flex", alignItems:"center", justifyContent:"center",
                color:"#fff", fontWeight:700, fontSize: c.isGroup ? 14 : 16
              }}>
                {c.isGroup ? <FiUsers size={18}/> : (c.name||"?")[0]}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <span style={{ fontWeight:600, fontSize:14, color:"#1e293b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:4 }}>
                    {c.isGroup && <span style={{ fontSize:10, background:"#3b82f6", color:"#fff", padding:"1px 5px", borderRadius:4, flexShrink:0 }}>مجموعة</span>}
                    {c.name}
                  </span>
                  <span style={{ fontSize:11, color:"#94a3b8", flexShrink:0, marginRight:6 }}>{fmtDate(c.timestamp)}</span>
                </div>
                <div style={{ fontSize:12, color:"#64748b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginTop:2 }}>
                  {c.lastMsg || "..."}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding:"8px 12px", borderTop:"1px solid #f1f5f9" }}>
          {/* شريط تقدم السحب الكامل */}
          {crawling && crawlProgress && (
            <div style={{ marginBottom:8, padding:"8px 10px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8 }}>
              <div style={{ fontSize:11, color:"#1d4ed8", fontWeight:600, marginBottom:4, display:"flex", justifyContent:"space-between" }}>
                <span>⏳ جاري السحب الكامل...</span>
                <span>دفعة {crawlProgress.currentBatch || 0}/{crawlProgress.totalBatches || "?"}</span>
              </div>
              <div style={{ height:6, background:"#dbeafe", borderRadius:4, overflow:"hidden" }}>
                <div style={{
                  height:"100%", background:"linear-gradient(90deg,#3b82f6,#2563eb)", borderRadius:4,
                  width: crawlProgress.totalBatches ? `${Math.min(100, (crawlProgress.currentBatch / crawlProgress.totalBatches) * 100)}%` : "10%",
                  transition:"width 0.5s ease"
                }}/>
              </div>
              <div style={{ fontSize:10, color:"#6b7280", marginTop:4, textAlign:"center" }}>
                {(crawlProgress.messagesStored || 0).toLocaleString("en-US")} رسالة · {crawlProgress.chatsProcessed || 0}/{crawlProgress.totalChatsAvailable || "?"} محادثة
              </div>
            </div>
          )}
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={doManualSync} disabled={syncing || crawling}
              style={{ flex:1, padding:"6px", background: syncing ? "#f1f5f9" : "#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, cursor: (syncing||crawling) ? "not-allowed" : "pointer", fontSize:12, color:"#64748b", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
              <FiRefreshCw size={12} className={syncing ? "wa-spin" : ""}/> {syncing ? "جاري..." : "🔄 مزامنة"}
            </button>
            <button onClick={startFullCrawl} disabled={crawling || syncing}
              style={{ flex:1, padding:"6px", background: crawling ? "#eff6ff" : "#f0fdf4", border: crawling ? "1px solid #bfdbfe" : "1px solid #bbf7d0", borderRadius:8, cursor: (crawling||syncing) ? "not-allowed" : "pointer", fontSize:12, color: crawling ? "#3b82f6" : "#16a34a", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontWeight:600 }}>
              {crawling ? "⏳ جاري السحب..." : "📥 سحب كامل"}
            </button>
          </div>
          {syncInfo?.last_sync_at && (
            <div style={{ fontSize:10, color:"#94a3b8", textAlign:"center", marginTop:4 }}>
              آخر مزامنة: {new Date(syncInfo.last_sync_at).toLocaleTimeString("ar-SA", { hour:"2-digit", minute:"2-digit" })}
              {" · "}{syncInfo.total_messages || 0} رسالة · تحديث تلقائي كل 5 دقائق
            </div>
          )}
        </div>
      </div>

      {/* ── نافذة المحادثة ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", background:"#f0f2f5", height:"100%", overflow:"hidden", minWidth:0 }}>
        {!selected ? (
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16, color:"#94a3b8" }}>
            <FaWhatsapp size={64} color="#d1d5db"/>
            <div style={{ fontSize:18, fontWeight:600, color:"#6b7280" }}>اختر محادثة للعرض</div>
            <div style={{ fontSize:13 }}>اختر من القائمة على اليمين لبدء المحادثة</div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ background:"#fff", padding:"12px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:12, boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
              <div style={{
                width:38, height:38, borderRadius:"50%",
                background: selected.isGroup ? "linear-gradient(135deg,#3b82f6,#1d4ed8)" : "linear-gradient(135deg,#25D366,#128C7E)",
                display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700
              }}>
                {selected.isGroup ? <FiUsers size={17}/> : (selected.name||"?")[0]}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:"#1e293b", display:"flex", alignItems:"center", gap:6 }}>
                  {selected.isGroup && <span style={{ fontSize:10, background:"#3b82f6", color:"#fff", padding:"1px 5px", borderRadius:4 }}>مجموعة</span>}
                  {selected.name}
                </div>
                <div style={{ fontSize:12, color:"#94a3b8", direction:"ltr" }}>
                  {/* v3.3.790 — LID ليس رقمًا */}
                  {selected.isGroup ? "مجموعة واتساب"
                    : String(selected.phone || "").length >= 14 ? "🔒 رقم مخفي (خصوصية واتساب)"
                    : `+${selected.phone}`}
                </div>
              </div>
              {selected.isGroup && (
                <button onClick={() => fetchGroupMembers(selected)}
                  style={{ padding:"5px 12px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, color:"#2563eb", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
                  <FiUsers size={13}/> أعضاء الجروب
                </button>
              )}
              <div style={{ flex:1 }}/>
              <button onClick={loadMessages} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:6 }}>
                <FiRefreshCw size={15}/>
              </button>
            </div>

            {/* الرسائل */}
            <div ref={msgContainerRef} style={{ flex:1, overflowY:"auto", padding:"16px 20px" }}>
              {loadingMsgs ? (
                <div style={{ textAlign:"center", color:"#94a3b8", padding:20 }}>جاري التحميل...</div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign:"center", color:"#94a3b8", padding:20, fontSize:13 }}>لا توجد رسائل</div>
              ) : (
                [...messages].reverse().map((msg, i) => (
                  <MessageBubble key={msg.id||i} msg={msg} contactPhone={selected.phone} isGroupChat={selected.isGroup} session={session}/>
                ))
              )}
              <div ref={endRef}/>
            </div>

            {/* مربع الإرسال */}
            <div style={{ background:"#fff", padding:"10px 16px", borderTop:"1px solid #e2e8f0" }}>
              {file && (
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:"#f0fdf4", borderRadius:8, marginBottom:8, fontSize:13 }}>
                  <FiFile size={14} color="#16a34a"/>
                  <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{file.name}</span>
                  <button onClick={()=>setFile(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", padding:2 }}><FiX size={14}/></button>
                </div>
              )}
              <div style={{ display:"flex", alignItems:"flex-end", gap:10 }}>
                <button onClick={()=>fileRef.current?.click()} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:"8px 4px", flexShrink:0 }}>
                  <FiPaperclip size={20}/>
                </button>
                <input ref={fileRef} type="file" style={{display:"none"}} onChange={e=>{ if(e.target.files[0]) setFile(e.target.files[0]); e.target.value=""; }}/>
                <textarea
                  value={text}
                  onChange={e=>setText(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();} }}
                  placeholder="اكتب رسالة..."
                  rows={1}
                  style={{ flex:1, border:"1px solid #e2e8f0", borderRadius:20, padding:"10px 16px", fontSize:14, fontFamily:"inherit", resize:"none", outline:"none", lineHeight:1.5, maxHeight:100, overflowY:"auto" }}
                />
                <button onClick={send} disabled={sending || (!text.trim() && !file)}
                  style={{ width:42, height:42, borderRadius:"50%", background: (sending||(!text.trim()&&!file)) ? "#94a3b8" : "#25D366", border:"none", cursor: (sending||(!text.trim()&&!file)) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {sending ? <span className="wa-spin"><FiRefreshCw size={16} color="#fff"/></span> : <FiSend size={16} color="#fff"/>}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Group Participants Modal */}
      {groupParticipants && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setGroupParticipants(null)}>
          <div dir="rtl" onClick={e => e.stopPropagation()}
            style={{ background:"#fff", borderRadius:14, width:420, maxHeight:"70vh", overflow:"auto", padding:24, boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:"#3b82f6" }}>👥 أعضاء الجروب</div>
                <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>💬 {groupParticipants.name}</div>
              </div>
              <button onClick={() => setGroupParticipants(null)} style={{ background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#94a3b8" }}>✕</button>
            </div>
            {groupParticipants.loading ? (
              <div style={{ textAlign:"center", padding:30, color:"#64748b", fontSize:13 }}>⏳ جاري سحب أعضاء الجروب...</div>
            ) : groupParticipants.error ? (
              <div style={{ textAlign:"center", padding:20 }}>
                <div style={{ fontSize:12, color:"#dc2626", fontWeight:600 }}>⚠️ {groupParticipants.error}</div>
              </div>
            ) : (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <span>✅ {groupParticipants.participants.length} عضو</span>
                </div>
                <div style={{ border:"1px solid #e2e8f0", borderRadius:8, overflow:"hidden" }}>
                  {groupParticipants.participants.length === 0 ? (
                    <div style={{ textAlign:"center", padding:24, color:"#94a3b8", fontSize:13 }}>لم يتم العثور على أعضاء</div>
                  ) : groupParticipants.participants.map((p, i) => (
                    <div key={p.phone + i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
                      borderBottom: i < groupParticipants.participants.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                      <div style={{ width:32, height:32, borderRadius:"50%", background:"#eff6ff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#3b82f6" }}>
                        {(p.name || p.phone || "?").charAt(0)}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12, fontWeight:600 }}>{p.name || "غير معروف"}</div>
                        <div style={{ fontSize:10, color:"#94a3b8", direction:"ltr" }}>{p.phone}</div>
                      </div>
                      {p.isAdmin && <span style={{ fontSize:9, background:"#fef3c7", color:"#92400e", padding:"2px 6px", borderRadius:8 }}>أدمن</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
            {!groupParticipants.loading && !groupParticipants.error && groupParticipants.participants.length > 0 && (
              <button onClick={async () => {
                try {
                  const res = await prospectsApi.bulkFromGroup({
                    members: groupParticipants.participants.map(p => ({ phone: (p.phone || "").replace(/[^0-9]/g, ""), name: p.name || "" })),
                    groupName: groupParticipants.name || "جروب واتساب"
                  });
                  const d = res.data;
                  toast.success(`تم السحب ✅ — جديد: ${d.added} | محدّث: ${d.updated} | تخطي: ${d.skipped}`);
                } catch (err) {
                  toast.error("فشل سحب الأعضاء للمحتملين");
                }
              }}
                style={{ marginTop:8, width:"100%", padding:"8px", background:"#10b981", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600 }}>
                📥 سحب الكل إلى المحتملين
              </button>
            )}
            <button onClick={() => setGroupParticipants(null)}
              style={{ marginTop:8, width:"100%", padding:"8px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12 }}>إغلاق</button>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
// OffersBroadcastTab — v3.3.307: إرسال عروض تسويقية مستقلة (بدون تحديثات)
// ═══════════════════════════════════════════════════════════════
function OffersBroadcastTab({ session, recipientType, contacts }) {
  const [selected,  setSelected]  = useState(new Set());
  const [selGroups, setSelGroups] = useState(new Set());
  const [message,   setMessage]   = useState("");
  const [search,    setSearch]    = useState("");
  const [sending,   setSending]   = useState(false);
  const [results,   setResults]   = useState([]);
  const [started,   setStarted]   = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [mode,      setMode]      = useState("contacts");
  const [groups,    setGroups]    = useState([]);
  const [loadingGrp, setLoadingGrp] = useState(false);
  const [clientGroups, setClientGroups] = useState([]);
  const [selClientGroups, setSelClientGroups] = useState(new Set());
  const [clientGroupMembers, setClientGroupMembers] = useState({});
  const [dailyStatus, setDailyStatus] = useState({ count: 0, limit: 50, remaining: 50, blocked: false });

  // Offers state
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [allOffers, setAllOffers] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  // تحميل قائمة المشاريع
  useEffect(() => {
    projectsApi.list().then(r => {
      const list = r.data?.projects || r.data || [];
      setProjectsList(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  // تحميل عروض المشروع المحدد
  const loadOffers = async (projectId) => {
    if (!projectId) { setAllOffers([]); setSelectedOffers([]); return; }
    setLoadingOffers(true);
    try {
      const r = await projectsApi.getProjectDocuments(projectId);
      const docs = r.data?.documents || [];
      const offers = docs.filter(d => d.doc_category === "offer");
      setAllOffers(offers);
    } catch { setAllOffers([]); }
    setLoadingOffers(false);
  };

  useEffect(() => {
    waApi.getDailyStatus().then(r => setDailyStatus(r.data)).catch(() => {});
  }, []);

  const refreshDailyStatus = () => { waApi.getDailyStatus().then(r => setDailyStatus(r.data)).catch(() => {}); };

  const groupsApi = recipientType === "marketer" ? mgApi : cgApi;
  const groupsLabel = recipientType === "marketer" ? "مجموعات المسوقين" : "مجموعات العملاء";

  useEffect(() => {
    (async () => {
      try {
        const res = await groupsApi.list();
        setClientGroups(res.data?.groups || []);
      } catch {}
    })();
  }, [recipientType]);

  useEffect(() => {
    (async () => {
      for (const gid of selClientGroups) {
        if (!clientGroupMembers[gid]) {
          try {
            const res = await groupsApi.getMembers(gid);
            setClientGroupMembers(prev => ({ ...prev, [gid]: res.data?.members || [] }));
          } catch {}
        }
      }
    })();
  }, [selClientGroups]);

  // سحب جروبات الواتساب
  useEffect(() => {
    (async () => {
      setLoadingGrp(true);
      try {
        const res = await waApi.getInbox(session);
        const chats = res.data?.chats || [];
        setGroups(chats.filter(c => c.isGroup));
      } catch {
        try {
          const res = await waApi.getChats(session);
          const chats = res.data?.chats || [];
          setGroups(chats.filter(c => c.isGroup).map(c => ({
            phone: c.id?.replace(/@.*$/, "") || c.id,
            name: c.name, isGroup: true, chatId: c.id,
          })));
        } catch {}
      }
      setLoadingGrp(false);
    })();
  }, [session]);

  const filteredContacts = contacts.filter(c =>
    !search || (c.name||"").toLowerCase().includes(search.toLowerCase()) || getPhone(c).includes(search)
  );
  const filteredGroups = groups.filter(g =>
    !search || (g.name||"").toLowerCase().includes(search.toLowerCase()) || (g.phone||"").includes(search)
  );
  const filteredClientGroups = clientGroups.filter(g =>
    !search || (g.name||"").toLowerCase().includes(search.toLowerCase())
  );

  const toggleAllContacts = () => {
    const ids = filteredContacts.filter(hasPhone).map(c => c.id);
    setSelected(selected.size === ids.length ? new Set() : new Set(ids));
  };
  const toggleAllGroups = () => {
    const ids = filteredGroups.map(g => g.phone);
    setSelGroups(selGroups.size === ids.length ? new Set() : new Set(ids));
  };
  const toggleAllClientGroups = () => {
    const ids = filteredClientGroups.map(g => g.id);
    setSelClientGroups(selClientGroups.size === ids.length ? new Set() : new Set(ids));
  };

  const resetBroadcast = () => { setStarted(false); setResults([]); setProgress(0); setSending(false); };

  const clientGroupMemberPhones = new Set();
  for (const gid of selClientGroups) {
    (clientGroupMembers[gid] || []).forEach(m => {
      if (m.phone) clientGroupMemberPhones.add(normalizePhone(m.phone));
    });
  }
  const totalSelected = selected.size + selGroups.size + (selClientGroups.size > 0 ? clientGroupMemberPhones.size : 0);

  const startSendOffers = async () => {
    if (totalSelected === 0) return toast.error("اختر مستلمين");
    if (selectedOffers.length === 0) return toast.error("اختر عرض واحد على الأقل");

    const contactTargets = contacts.filter(c => selected.has(c.id) && hasPhone(c)).map(c => ({
      type: "contact", name: c.name, phone: normalizePhone(getPhone(c)),
      recipientType, recipientId: c.id, recipientName: c.name,
    }));

    const cgMemberTargets = [];
    const cgPhonesSeen = new Set(contactTargets.map(t => t.phone));
    for (const gid of selClientGroups) {
      (clientGroupMembers[gid] || []).forEach(m => {
        if (m.phone) {
          const phone = normalizePhone(m.phone);
          if (!cgPhonesSeen.has(phone)) {
            cgPhonesSeen.add(phone);
            cgMemberTargets.push({
              type: "contact", name: m.name, phone,
              recipientType, recipientId: m.id, recipientName: m.name,
            });
          }
        }
      });
    }

    const groupTargets = groups.filter(g => selGroups.has(g.phone)).map(g => ({
      type: "group", name: g.name, phone: g.phone,
      chatId: g.chatId || (g.phone + "@g.us"), isGroup: true,
    }));
    const allTargets = [...contactTargets, ...cgMemberTargets, ...groupTargets];

    const initialResults = allTargets.map(t => ({ contact: { name: t.name, phone: t.phone }, status: "pending", error: "" }));
    setResults(initialResults);
    setStarted(true);
    setSending(true);

    for (let i = 0; i < allTargets.length; i++) {
      const t = allTargets[i];
      const introMsg = personalize(message || "", { name: t.name });
      try {
        // 1. إرسال نص تعريفي اختياري
        if (introMsg.trim()) {
          const meta = t.isGroup ? { chatId: t.chatId, isGroup: true } : {
            recipientType: t.recipientType, recipientId: t.recipientId, recipientName: t.recipientName,
          };
          await waApi.sendMessage(session, t.phone, introMsg, meta);
          await new Promise(r => setTimeout(r, 2000));
        }
        // 2. إرسال كل عرض
        for (let oi = 0; oi < selectedOffers.length; oi++) {
          const offer = selectedOffers[oi];
          const linkedModels = (offer.linked_unit_models || "").split("|").filter(Boolean);
          const modelsCaption = linkedModels.length > 0 ? `\n🏠 ${linkedModels.join("، ")}` : "";
          const arabicCaption = `🎁 ${offer.title || "عرض تسويقي"}${modelsCaption}`;
          if (offer.drive_file_id) {
            await waApi.sendDriveFile(session, {
              phone: t.phone,
              chatId: t.isGroup ? (t.chatId || (t.phone + "@g.us")) : undefined,
              isGroup: t.isGroup || false,
              driveFileId: offer.drive_file_id,
              caption: arabicCaption,
              recipientType: t.recipientType || "",
              recipientName: t.name || "",
            });
          } else if (offer.drive_web_link) {
            const linkMsg = `${arabicCaption}\n${offer.drive_web_link}`;
            const meta = t.isGroup ? { chatId: t.chatId, isGroup: true } : {};
            await waApi.sendMessage(session, t.phone, linkMsg, meta);
          }
          if (oi < selectedOffers.length - 1) await new Promise(r => setTimeout(r, 3000));
        }
        setResults(prev => { const n=[...prev]; n[i]={...n[i],status:"sent"}; return n; });
      } catch (e) {
        setResults(prev => { const n=[...prev]; n[i]={...n[i],status:"failed",error:e.response?.data?.error||e.message}; return n; });
      }
      setProgress(i + 1);
      if (i < allTargets.length - 1) {
        const delay = 7000 + Math.floor(Math.random() * 5000);
        await new Promise(r => setTimeout(r, delay));
      }
    }
    setSending(false);
    refreshDailyStatus();
  };

  const sentCount = results.filter(r => r.status === "sent").length;

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {!started ? (
        <div style={{ flex:1, overflow:"auto", padding:20 }}>
          {/* Banner */}
          <div style={{ marginBottom:14, padding:"10px 14px", background:"linear-gradient(135deg, #fef3c7, #fde68a)", border:"1px solid #fcd34d", borderRadius:10 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#78350f", marginBottom:4 }}>🎁 إرسال عروض تسويقية</div>
            <div style={{ fontSize:11, color:"#92400e" }}>اختر مشروع، حدّد العروض المراد إرسالها (اختيار يدوي)، ثم اختر المستلمين. يمكن إضافة نص تعريفي اختياري.</div>
          </div>

          {/* Project + Offers Selector */}
          <div style={{ marginBottom:16, border:"1px solid #fcd34d", borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"8px 14px", background:"#fef3c7", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#78350f" }}>🏗️ اختر مشروع</span>
              <select value={selectedProject || ""} onChange={e => { setSelectedProject(e.target.value || null); loadOffers(e.target.value); setSelectedOffers([]); }}
                style={{ flex:1, padding:"5px 8px", border:"1px solid #fcd34d", borderRadius:6, fontSize:12, fontFamily:"inherit", background:"#fff" }}>
                <option value="">— اختر مشروع —</option>
                {projectsList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {selectedProject && (
              <div style={{ padding:10 }}>
                {loadingOffers ? (
                  <div style={{ textAlign:"center", padding:10, color:"#94a3b8", fontSize:12 }}>⏳ جاري التحميل...</div>
                ) : allOffers.length === 0 ? (
                  <div style={{ textAlign:"center", padding:14, color:"#94a3b8", fontSize:12 }}>لا توجد عروض تسويقية لهذا المشروع. أضف عروض من تبويب "مستندات المشروع".</div>
                ) : (
                  <>
                    <div style={{ fontSize:11, color:"#78350f", marginBottom:6, fontWeight:700 }}>اختر العروض المراد إرسالها ({selectedOffers.length} / {allOffers.length}):</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      {allOffers.map(offer => {
                        const isSel = selectedOffers.some(o => o.id === offer.id);
                        const linkedModels = (offer.linked_unit_models || "").split("|").filter(Boolean);
                        return (
                          <label key={offer.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderRadius:8, cursor:"pointer", fontSize:12,
                            background: isSel ? "#fffbeb" : "transparent", border: isSel ? "1px solid #fcd34d" : "1px solid #f1f5f9" }}>
                            <input type="checkbox" checked={isSel}
                              onChange={e => setSelectedOffers(prev => e.target.checked
                                ? [...prev, offer]
                                : prev.filter(o => o.id !== offer.id)
                              )} style={{ width:14, height:14, accentColor:"#f59e0b" }} />
                            <span style={{ flex:1, fontWeight:isSel?700:500 }}>🎁 {offer.title}</span>
                            {linkedModels.length > 0 && (
                              <span style={{ fontSize: 10, color: "#92400e", fontWeight:600 }}>🏠 {linkedModels.join("، ")}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Optional intro message */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>
              ✍️ نص تعريفي قبل العروض <span style={{ fontWeight:400, color:"#94a3b8" }}>(اختياري — {"{الاسم}"} = اسم تلقائي)</span>
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
              placeholder="مثال: مرحباً {الاسم}، نقدم لك أحدث عروضنا الحصرية..."
              style={{
                width:"100%", boxSizing:"border-box", padding:"12px 14px",
                border:"2px solid #e2e8f0", borderRadius:10, fontFamily:"inherit", fontSize:14, resize:"vertical"
              }}/>
          </div>

          {/* Mode selector */}
          <div style={{ display:"flex", gap:6, marginBottom:12, padding:4, background:"#f1f5f9", borderRadius:10 }}>
            <button onClick={() => setMode("contacts")} style={{
              flex:1, padding:"8px 12px", background: mode==="contacts" ? "#fff" : "transparent",
              border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:mode==="contacts"?700:500,
              boxShadow: mode==="contacts" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", fontFamily:"inherit"
            }}>👤 جهات اتصال ({selected.size})</button>
            <button onClick={() => setMode("clientGroups")} style={{
              flex:1, padding:"8px 12px", background: mode==="clientGroups" ? "#fff" : "transparent",
              border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:mode==="clientGroups"?700:500,
              boxShadow: mode==="clientGroups" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", fontFamily:"inherit"
            }}>📋 {groupsLabel} ({selClientGroups.size})</button>
            <button onClick={() => setMode("waGroups")} style={{
              flex:1, padding:"8px 12px", background: mode==="waGroups" ? "#fff" : "transparent",
              border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:mode==="waGroups"?700:500,
              boxShadow: mode==="waGroups" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", fontFamily:"inherit"
            }}>👥 جروبات واتساب ({selGroups.size})</button>
          </div>

          {/* Search + Recipients */}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 بحث..."
            style={{ width:"100%", boxSizing:"border-box", padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, marginBottom:8, fontFamily:"inherit" }}/>

          {mode === "contacts" && (
            <div>
              <button onClick={toggleAllContacts} style={{ padding:"6px 14px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontSize:12, fontFamily:"inherit", marginBottom:8 }}>
                {selected.size === filteredContacts.filter(hasPhone).length ? "إلغاء الكل" : "تحديد الكل"}
              </button>
              <div style={{ maxHeight:280, overflow:"auto", border:"1px solid #e2e8f0", borderRadius:8 }}>
                {filteredContacts.map(c => {
                  const phone = getPhone(c);
                  const has = !!phone;
                  return (
                    <label key={c.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderBottom:"1px solid #f1f5f9", cursor: has?"pointer":"not-allowed", opacity: has?1:0.5 }}>
                      <input type="checkbox" disabled={!has} checked={selected.has(c.id)}
                        onChange={() => { const n=new Set(selected); n.has(c.id)?n.delete(c.id):n.add(c.id); setSelected(n); }}
                        style={{ width:14, height:14, accentColor:"#f59e0b" }} />
                      <span style={{ flex:1, fontSize:13 }}>{c.name}</span>
                      <span style={{ fontSize:11, color:"#94a3b8", direction:"ltr" }}>{phone || "بدون رقم"}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {mode === "clientGroups" && (
            <div>
              <button onClick={toggleAllClientGroups} style={{ padding:"6px 14px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontSize:12, fontFamily:"inherit", marginBottom:8 }}>
                {selClientGroups.size === filteredClientGroups.length ? "إلغاء الكل" : "تحديد الكل"}
              </button>
              <div style={{ maxHeight:280, overflow:"auto", border:"1px solid #e2e8f0", borderRadius:8 }}>
                {filteredClientGroups.map(g => (
                  <label key={g.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderBottom:"1px solid #f1f5f9", cursor:"pointer" }}>
                    <input type="checkbox" checked={selClientGroups.has(g.id)}
                      onChange={() => { const n=new Set(selClientGroups); n.has(g.id)?n.delete(g.id):n.add(g.id); setSelClientGroups(n); }}
                      style={{ width:14, height:14, accentColor:"#f59e0b" }} />
                    <span style={{ flex:1, fontSize:13 }}>📋 {g.name}</span>
                    <span style={{ fontSize:11, color:"#94a3b8" }}>{(clientGroupMembers[g.id]||[]).length} عضو</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {mode === "waGroups" && (
            <div>
              <button onClick={toggleAllGroups} style={{ padding:"6px 14px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontSize:12, fontFamily:"inherit", marginBottom:8 }}>
                {selGroups.size === filteredGroups.length ? "إلغاء الكل" : "تحديد الكل"}
              </button>
              <div style={{ maxHeight:280, overflow:"auto", border:"1px solid #e2e8f0", borderRadius:8 }}>
                {loadingGrp ? <div style={{ textAlign:"center", padding:14, color:"#94a3b8" }}>⏳ جاري تحميل الجروبات...</div> :
                  filteredGroups.length === 0 ? <div style={{ textAlign:"center", padding:14, color:"#94a3b8" }}>لا توجد جروبات</div> :
                  filteredGroups.map(g => (
                    <label key={g.phone} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderBottom:"1px solid #f1f5f9", cursor:"pointer" }}>
                      <input type="checkbox" checked={selGroups.has(g.phone)}
                        onChange={() => { const n=new Set(selGroups); n.has(g.phone)?n.delete(g.phone):n.add(g.phone); setSelGroups(n); }}
                        style={{ width:14, height:14, accentColor:"#f59e0b" }} />
                      <span style={{ flex:1, fontSize:13 }}>👥 {g.name}</span>
                    </label>
                  ))
                }
              </div>
            </div>
          )}

          {/* Send button */}
          <button onClick={startSendOffers}
            disabled={totalSelected === 0 || selectedOffers.length === 0 || dailyStatus.blocked}
            style={{
              marginTop:14, width:"100%", padding:14,
              background: (totalSelected === 0 || selectedOffers.length === 0 || dailyStatus.blocked) ? "#cbd5e1" : "linear-gradient(135deg, #f59e0b, #d97706)",
              color:"#fff", border:"none", borderRadius:10,
              fontSize:14, fontWeight:700, cursor:(totalSelected === 0 || selectedOffers.length === 0 || dailyStatus.blocked) ? "not-allowed" : "pointer",
              fontFamily:"inherit", boxShadow:"0 4px 12px rgba(245,158,11,0.3)"
            }}>
            🚀 إرسال {selectedOffers.length} عرض إلى {totalSelected} مستلم
          </button>

          {/* Daily counter */}
          <div style={{ marginTop:8, fontSize:11, color: dailyStatus.blocked ? "#dc2626" : "#64748b", textAlign:"center" }}>
            📊 الإرسال اليومي: {dailyStatus.count} / {dailyStatus.limit} {dailyStatus.blocked && "(محظور حتى منتصف الليل)"}
          </div>
          <button onClick={resetBroadcast} style={{
            marginTop:6, width:"100%", padding:6, background:"none", border:"1px solid #e2e8f0",
            borderRadius:8, fontSize:11, color:"#64748b", cursor:"pointer", fontFamily:"inherit"
          }}>تفريغ النموذج</button>
        </div>
      ) : (
        /* Progress view */
        <div style={{ flex:1, overflow:"auto", padding:20 }}>
          <div style={{ background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10, padding:14, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:14, fontWeight:700, color:"#78350f" }}>{sending ? "🚀 جاري الإرسال..." : "✅ اكتمل الإرسال"}</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#16a34a" }}>{sentCount} / {results.length}</span>
            </div>
            <div style={{ height:6, background:"#fef3c7", borderRadius:3, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${(progress/results.length)*100}%`, background:"linear-gradient(90deg, #f59e0b, #d97706)", transition:"width 0.3s" }}/>
            </div>
          </div>
          <div style={{ maxHeight:380, overflow:"auto", border:"1px solid #e2e8f0", borderRadius:8 }}>
            {results.map((r, i) => (
              <div key={i} style={{ padding:"8px 12px", borderBottom:"1px solid #f1f5f9", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:14 }}>{r.status === "sent" ? "✅" : r.status === "failed" ? "❌" : "⏳"}</span>
                <span style={{ flex:1, fontSize:13 }}>{r.contact.name}</span>
                <span style={{ fontSize:11, color:"#94a3b8", direction:"ltr" }}>{r.contact.phone}</span>
                {r.error && <span style={{ fontSize:10, color:"#dc2626" }}>{r.error}</span>}
              </div>
            ))}
          </div>
          {!sending && (
            <button onClick={resetBroadcast} style={{
              marginTop:14, padding:"10px 20px", background:"#f59e0b", color:"#fff", border:"none",
              borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit"
            }}>إرسال جديد</button>
          )}
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function CommunicationPage({ defaultTab = "clients", personal = false }) { // v3.4.343 — personal: محادثات فقط (واتساب الرئيس)
  // وضع مفرد — يُفعَّل عند الوصول من رابط مباشر (تواصل العملاء / تواصل المسوقين)
  const isSingleMode = defaultTab === "clients" || defaultTab === "marketers" || defaultTab === "abubader2" || defaultTab === "abubader_biz";
  const [clients,   setClients]   = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [innerTab,  setInnerTab]  = useState("inbox"); // "inbox" | "contact" | "broadcast"
  const tabNow = personal ? "inbox" : innerTab; // v3.4.343
  const [waStatus,  setWaStatus]  = useState("loading"); // "connected" | "disconnected" | "loading"
  const [waPhone,   setWaPhone]   = useState(null);
  const [waRestarting, setWaRestarting] = useState(false);

  const handleWaRestart = async () => {
    const sessionId = defaultTab === "marketers" ? "marketers" : defaultTab === "abubader2" ? "abubader2" : defaultTab === "abubader_biz" ? "abubader_biz" : "corporate";
    setWaRestarting(true);
    try {
      await waApi.restart(sessionId);
      toast.success("تم إرسال أمر إعادة التشغيل");
      setTimeout(async () => {
        try {
          const res = await waApi.getStatus(sessionId);
          const d = res.data;
          setWaStatus(d.status === "CONNECTED" || d.connected ? "connected" : "disconnected");
          setWaPhone(d.phone || null);
        } catch { setWaStatus("disconnected"); }
        setWaRestarting(false);
      }, 5000);
    } catch {
      toast.error("فشل إعادة التشغيل");
      setWaRestarting(false);
    }
  };
  const [clientTemplates, setClientTemplates] = useState(CLIENT_TEMPLATES_FALLBACK);
  const [marketerTemplates, setMarketerTemplates] = useState(MARKETER_TEMPLATES_FALLBACK);

  // تحميل القوالب من قاعدة البيانات
  useEffect(() => {
    (async () => {
      try {
        const [cRes, mRes] = await Promise.all([
          commApi.getTemplates("clients").catch(() => ({ data: { templates: [] } })),
          commApi.getTemplates("marketers").catch(() => ({ data: { templates: [] } })),
        ]);
        const ct = cRes.data?.templates || [];
        const mt = mRes.data?.templates || [];
        if (ct.length > 0) setClientTemplates(ct);
        if (mt.length > 0) setMarketerTemplates(mt);
      } catch {}
    })();
  }, []);

  // مزامنة activeTab عند تغيير defaultTab (التنقل بين تواصل العملاء / المسوقين)
  useEffect(() => {
    setActiveTab(defaultTab);
    setInnerTab("inbox");
  }, [defaultTab]);

  // حالة اتصال الواتساب
  useEffect(() => {
    const sessionId = defaultTab === "marketers" ? "marketers" : defaultTab === "abubader2" ? "abubader2" : defaultTab === "abubader_biz" ? "abubader_biz" : "corporate";
    const check = async () => {
      try {
        const res = await waApi.getStatus(sessionId);
        const d = res.data;
        setWaStatus(d.status === "CONNECTED" || d.connected ? "connected" : "disconnected");
        setWaPhone(d.phone || null);
      } catch {
        setWaStatus("disconnected");
        setWaPhone(null);
      }
    };
    check();
    const iv = setInterval(check, 15000);
    return () => clearInterval(iv);
  }, [defaultTab]);

  useEffect(() => {
    (async () => {
      try {
        const [cr, mr] = await Promise.all([
          refApi.getClients(),
          refApi.getMarketers(),
        ]);
        setClients(cr.data.clients || cr.data.items || []);
        setMarketers(mr.data.marketers || mr.data.items || []);
      } catch {
        toast.error("فشل تحميل البيانات");
      }
      setLoading(false);
    })();
  }, []);

  const tabs = [
    {
      key: "clients",
      label: "تواصل عملاء الشركة",
      icon: <FiUsers size={16}/>,
      session: "corporate",
      recipientType: "client",
      contacts: clients,
      templates: clientTemplates,
    },
    {
      key: "marketers",
      label: "تواصل المسوقين",
      icon: <FiUserCheck size={16}/>,
      session: "marketers",
      recipientType: "marketer",
      contacts: marketers,
      templates: marketerTemplates,
    },
    {
      key: "abubader2",
      label: "تواصل ابوبدر 2",
      icon: <FiUserCheck size={16}/>,
      session: "abubader2",
      recipientType: "marketer",
      contacts: marketers,
      templates: marketerTemplates,
    },
    {
      key: "abubader_biz",
      label: "تواصل ابوبدر بزنس",
      icon: <FiUserCheck size={16}/>,
      session: "abubader_biz",
      recipientType: "marketer",
      contacts: marketers,
      templates: marketerTemplates,
    },
  ];

  const active = tabs.find(t => t.key === activeTab);

  if (loading) return (
    <div style={{ height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f0f2f5" }}>
      <div style={{ textAlign:"center" }}>
        <FaWhatsapp size={60} color="#25D366" style={{ marginBottom:14 }}/>
        <div style={{ color:"#64748b", fontSize:15 }}>جاري تحميل مركز التواصل...</div>
      </div>
    </div>
  );

  return (
    <div style={{ height:"100vh", display:"flex", flexDirection:"column", background:"#fff" }}>
      <style>{`
        @keyframes wa-rot { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        .wa-spin { display:inline-block; animation:wa-rot 0.9s linear infinite; }
      `}</style>

      {/* Top tabs — تُخفى في الوضع المفرد */}
      <div style={{
        background:"linear-gradient(135deg,#075E54 0%,#128C7E 100%)",
        display:"flex", alignItems:"stretch",
        boxShadow:"0 2px 12px rgba(0,0,0,0.2)", flexShrink:0
      }}>
        <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:10 }}>
          <FaWhatsapp size={24} color="#25D366"/>
          <span style={{ fontWeight:700, fontSize:16, color:"#fff" }}>
            {isSingleMode ? active?.label : "مركز التواصل"}
          </span>
          {isSingleMode && (
            <span style={{
              display:"inline-flex", alignItems:"center", gap:5, fontSize:12, fontWeight:600,
              padding:"3px 10px", borderRadius:20, marginRight:6,
              background: waStatus === "connected" ? "rgba(37,211,102,0.2)" : "rgba(239,68,68,0.2)",
              color: waStatus === "connected" ? "#86efac" : "#fca5a5",
              border: `1px solid ${waStatus === "connected" ? "rgba(37,211,102,0.3)" : "rgba(239,68,68,0.3)"}`,
            }}>
              {waStatus === "connected" ? <FiWifi size={11}/> : <FiWifiOff size={11}/>}
              {waStatus === "connected" ? "متصل" : "غير متصل"}
              {waPhone && <span style={{ direction:"ltr", opacity:0.8 }}>• {waPhone.replace(/^966/, "0")}</span>}
            </span>
          )}
          {isSingleMode && (
            <button onClick={handleWaRestart} disabled={waRestarting}
              style={{
                display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600,
                padding:"4px 10px", borderRadius:16, cursor: waRestarting ? "wait" : "pointer",
                background:"rgba(255,255,255,0.15)", color:"#fff", border:"1px solid rgba(255,255,255,0.2)",
                opacity: waRestarting ? 0.6 : 1, fontFamily:"inherit",
              }}>
              <FiRefreshCw size={11} style={{ animation: waRestarting ? "spin 1s linear infinite" : "none" }}/>
              {waRestarting ? "جاري..." : "إعادة تشغيل"}
            </button>
          )}
        </div>
        <div style={{ flex:1 }}/>

        {/* تابات الوضع المفرد: تواصل + متابعة — v3.4.343: تُخفى في الوضع الشخصي (محادثات فقط) */}
        {isSingleMode && !personal && (
          <>
            <button onClick={() => setInnerTab("inbox")} style={{
              padding:"0 22px", background: innerTab==="inbox" ? "rgba(255,255,255,0.15)" : "transparent",
              border:"none", borderBottom: innerTab==="inbox" ? "3px solid #25D366" : "3px solid transparent",
              cursor:"pointer", color:"#fff", display:"flex", alignItems:"center", gap:8,
              fontSize:14, fontWeight: innerTab==="inbox" ? 700 : 500, fontFamily:"inherit"
            }}>
              <FiRadio size={15}/> متابعة المحادثات
            </button>
            <button onClick={() => setInnerTab("contact")} style={{
              padding:"0 22px", background: innerTab==="contact" ? "rgba(255,255,255,0.15)" : "transparent",
              border:"none", borderBottom: innerTab==="contact" ? "3px solid #25D366" : "3px solid transparent",
              cursor:"pointer", color:"#fff", display:"flex", alignItems:"center", gap:8,
              fontSize:14, fontWeight: innerTab==="contact" ? 700 : 500, fontFamily:"inherit"
            }}>
              <FiUsers size={15}/> تواصل مع {activeTab==="clients" ? "العملاء" : "المسوقين"}
              <span style={{ background: innerTab==="contact" ? "#25D366" : "rgba(255,255,255,0.2)", color:"#fff", padding:"2px 8px", borderRadius:12, fontSize:12, fontWeight:700 }}>
                {active?.contacts?.length || 0}
              </span>
            </button>
            <button onClick={() => setInnerTab("broadcast")} style={{
              padding:"0 22px", background: innerTab==="broadcast" ? "rgba(255,255,255,0.15)" : "transparent",
              border:"none", borderBottom: innerTab==="broadcast" ? "3px solid #25D366" : "3px solid transparent",
              cursor:"pointer", color:"#fff", display:"flex", alignItems:"center", gap:8,
              fontSize:14, fontWeight: innerTab==="broadcast" ? 700 : 500, fontFamily:"inherit"
            }}>
              <FiRadio size={15}/> بث جماعي
              <span style={{ background: innerTab==="broadcast" ? "#25D366" : "rgba(255,255,255,0.2)", color:"#fff", padding:"2px 8px", borderRadius:12, fontSize:12, fontWeight:700 }}>
                {active?.contacts?.filter(hasPhone).length || 0}
              </span>
            </button>
            {/* v3.3.307: تبويب إرسال عروض مستقل */}
            <button onClick={() => setInnerTab("offers")} style={{
              padding:"0 22px", background: innerTab==="offers" ? "rgba(255,255,255,0.15)" : "transparent",
              border:"none", borderBottom: innerTab==="offers" ? "3px solid #f59e0b" : "3px solid transparent",
              cursor:"pointer", color:"#fff", display:"flex", alignItems:"center", gap:8,
              fontSize:14, fontWeight: innerTab==="offers" ? 700 : 500, fontFamily:"inherit"
            }}>
              🎁 إرسال عروض
            </button>
          </>
        )}

        {/* تابات الوضع الكامل */}
        {!isSingleMode && tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding:"0 24px",
            background: activeTab===tab.key ? "rgba(255,255,255,0.15)" : "transparent",
            border:"none",
            borderBottom: activeTab===tab.key ? "3px solid #25D366" : "3px solid transparent",
            cursor:"pointer", color:"#fff",
            display:"flex", alignItems:"center", gap:8,
            fontSize:14, fontWeight: activeTab===tab.key ? 700 : 500,
            fontFamily:"inherit", transition:"background 0.2s"
          }}>
            {tab.icon} {tab.label}
            <span style={{
              background: activeTab===tab.key ? "#25D366" : "rgba(255,255,255,0.2)",
              color:"#fff", padding:"2px 8px", borderRadius:12, fontSize:12, fontWeight:700
            }}>{tab.contacts.length}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex:1, overflow:"hidden" }}>
        {/* وضع مفرد — تاب تواصل أو inbox */}
        {isSingleMode && active && tabNow === "contact" && (
          <SessionTab
            key={active.key}
            session={active.session}
            label={active.label}
            recipientType={active.recipientType}
            contacts={active.contacts}
            templates={active.templates}
          />
        )}
        {isSingleMode && active && tabNow === "inbox" && (
          <InboxTab session={active.session} personal={personal}/>
        )}
        {isSingleMode && active && tabNow === "broadcast" && (
          <BroadcastTab
            session={active.session}
            recipientType={active.recipientType}
            contacts={active.contacts}
            templates={active.templates}
          />
        )}
        {/* v3.3.307: تبويب إرسال عروض */}
        {isSingleMode && active && tabNow === "offers" && (
          <OffersBroadcastTab
            session={active.session}
            recipientType={active.recipientType}
            contacts={active.contacts}
          />
        )}
        {/* وضع كامل */}
        {!isSingleMode && active && (
          <SessionTab
            key={active.key}
            session={active.session}
            label={active.label}
            recipientType={active.recipientType}
            contacts={active.contacts}
            templates={active.templates}
          />
        )}
      </div>
    </div>
  );
}
